"use client"

import { ExpandableQuestion } from "../../../components/expandable-question"
import { LeadStatusMenu } from "../../../components/lead-category/lead-status-menu"
import { fill } from "../../../lib/fill"
import { useAutoleadsLocale, useDateLocale } from "../../../lib/locale-context"
import { useTr } from "../../../lib/use-tr"
import * as AccordionPrimitive from "@radix-ui/react-accordion"
import {
  ArrowUpRight,
  BadgeCheck,
  BookText,
  Briefcase,
  Building2,
  Check,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  CloudOff,
  ExternalLink,
  FileCode,
  FileText,
  Gauge,
  Globe,
  Layers,
  Link,
  Linkedin,
  Loader2,
  Lock,
  Mail,
  MapPin,
  MapPinned,
  Megaphone,
  MegaphoneOff,
  Minus,
  Palette,
  Pencil,
  Phone,
  PhoneCall,
  Plus,
  Radar,
  Search,
  Send,
  Share2,
  ShieldAlert,
  Sparkles,
  Star,
  Trash2,
  Users,
  Wallet,
} from "lucide-react"
import type { LucideIcon } from "lucide-react"
import { type ComponentType, type ReactNode, useId, useState } from "react"
import type { IconType } from "react-icons"
import {
  FaFacebook,
  FaGoogle,
  FaInstagram,
  FaLinkedin,
  FaTiktok,
  FaXTwitter,
  FaYoutube,
} from "react-icons/fa6"

import {
  type CustomQueryVerdict,
  customQueryStatement,
  customQueryVerdict,
  entryVerdict,
} from "@/lib/apps/autoleads/custom-query"
import { useDeleteLeadContact } from "@/lib/apps/autoleads/hooks/use-lead-contact-mutations"
import { useScrollHint } from "@/lib/apps/autoleads/hooks/use-scroll-hint"
import { summarizeRatings } from "@/lib/apps/autoleads/lead-ratings"
import {
  type SequenceStep,
  sequenceSteps,
} from "@/lib/apps/autoleads/mailing-sequence"
import { ordinal } from "@/lib/apps/autoleads/ordinal"
import { useCurrentUser } from "@/lib/nango.user/hooks/use-user-queries"
import { isHttpUrl } from "@/lib/safe-url"
import { cn } from "@/lib/utils"

import { PhoneLink } from "@/components/nango/autoleads/phone-link"
import { SiteFavicon } from "@/components/nango/autoleads/site-favicon"
import { Accordion, AccordionItem } from "@/components/ui/accordion"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip"

import { LinkedBody } from "@/app/autoleads/components/linked-body"
import { ScrollHintArrow } from "@/app/autoleads/components/scroll-hint-arrow"

import { LeadContactDialog } from "./contact-form-dialog"
import { LeadAuditPdfButton } from "./lead-audit-pdf-button"
import { LeadDownloadMenu } from "./lead-download-menu"
import { LeadExtractionBadge } from "./lead-extraction-badge"
import { LeadPilotBar } from "./lead-pilot-bar"
import {
  LeadRatingChip,
  LeadRatingDetails,
  SIGNAL_LINK_ICON_CLASS,
} from "./lead-rating-badge"
import {
  LeadSecurityMeta,
  LeadSecurityReport,
  LeadSecurityTitle,
  SecurityPdfButton,
} from "./lead-security-report"
import { LEADS_PANE_T, REENRICH_T } from "./translations"
import {
  WatcherEventBadges,
  WatcherEventDetails,
  watcherEventLabelKey,
} from "./watcher-event"
import type {
  CustomEnrichment,
  DecisionMakerEntry,
  Lead,
  LeadPerson,
  LeadPhoneContact,
  LeadsAdCheck,
  LeadsAdCheckRegistry,
  LeadsAdCheckRegistryKey,
  LeadsAdSignal,
  LeadsGeoQuestion,
  LeadsJobOffer,
  LeadsJobOffersChecked,
  LeadsKeywordOpportunity,
  LeadsOpportunityCoverage,
  LeadsOpportunityRank,
  LeadsOpportunityReport as LeadsOpportunityReportType,
  LeadsOpportunityTheme,
  LeadsRankCheck,
  LeadsSocialPlatform,
  LeadsSocials,
  PappersFiche,
  PappersFicheRow,
  PappersFicheSection,
} from "@/nango/types/apps.autoleads/autoleads/AutoLeads"

const URL_MAX_LEN = 60
// PageSpeed Insights runs the audit live from its own page, so the link carries
// the audited URL and the form factor whose numbers the row quotes (mobile).
const PAGESPEED_REPORT_URL = "https://pagespeed.web.dev/analysis"
const ENRICHMENT_MAX_VISIBLE = 3
// Sites listed in a GEO question's popover before the "+N" line.
const GEO_SOURCES_MAX_VISIBLE = 8
// Contacts listed before the "+N" toggle, best-ranked first.
const PEOPLE_MAX_VISIBLE = 3
// Phone lines listed before the "+N" toggle. Same cap as the contacts column
// beside it, so both toggles sit at the same height.
const PHONE_ROSTER_MAX_VISIBLE = PEOPLE_MAX_VISIBLE
// Offers listed before the "+N" toggle. A business hiring more than this is
// already a clear enough signal without stretching the card.
const JOB_OFFERS_MAX_VISIBLE = 5

// Display names for the sources the plugin reports having checked. Board names
// are brands and read the same in both languages; only the two generic buckets
// are translated. A source added backend-side later falls back to its raw slug.
const JOB_OFFER_SOURCE_BRANDS: Record<string, string> = {
  linkedin: "LinkedIn",
  indeed: "Indeed",
  welcometothejungle: "Welcome to the Jungle",
  apec: "APEC",
  hellowork: "HelloWork",
}

const JOB_OFFER_SOURCE_LABEL_KEYS: Record<
  string,
  keyof typeof LEADS_PANE_T & string
> = {
  website: "jobOffersSourceWebsite",
  other: "jobOffersSourceOther",
}

function jobOfferSourceLabel(source: string, tr: LeadsPaneTr): string {
  const key = JOB_OFFER_SOURCE_LABEL_KEYS[source]
  if (key) return tr(key)
  return JOB_OFFER_SOURCE_BRANDS[source] ?? source
}

type LeadsPaneTr = (key: keyof typeof LEADS_PANE_T & string) => string

// Placement bands the keyword table colours by. They mirror the backend's
// SEO_RANK_TOP_POSITIONS and one page of Google results.
const KEYWORD_RANK_TOP = 3
const KEYWORD_RANK_PAGE_ONE = 10

export const OPPORTUNITY_THEME_LABELS: Record<
  LeadsOpportunityTheme,
  keyof typeof LEADS_PANE_T & string
> = {
  money: "opportunityThemeMoney",
  google_profile: "opportunityThemeGoogleProfile",
  visibility_geo: "opportunityThemeVisibilityGeo",
  visibility_keyword: "opportunityThemeVisibilityKeyword",
  seo_technical: "opportunityThemeSeoTechnical",
  conversion: "opportunityThemeConversion",
  speed: "opportunityThemeSpeed",
  design: "opportunityThemeDesign",
  acquisition: "opportunityThemeAcquisition",
  trust: "opportunityThemeTrust",
  compliance: "opportunityThemeCompliance",
  visibility_ai: "opportunityThemeVisibilityAi",
}

export const OPPORTUNITY_THEME_ICONS: Record<
  LeadsOpportunityTheme,
  LucideIcon
> = {
  money: Wallet,
  google_profile: MapPinned,
  visibility_geo: MapPin,
  visibility_keyword: Search,
  seo_technical: FileCode,
  conversion: PhoneCall,
  speed: Gauge,
  design: Palette,
  acquisition: Megaphone,
  trust: BadgeCheck,
  compliance: ShieldAlert,
  visibility_ai: Sparkles,
}

type OpportunitySeverity =
  LeadsOpportunityReportType["themes"][number]["severity"]

// One severity vocabulary for the whole card. Red and orange rank the site's
// problems by urgency. The "positive" step only ever holds buying signals (can
// they pay, do they already spend on customers), which say nothing about the
// site, so it stays neutral: green read as "this part of the site is fine".
// Stated once as a group heading, so no row has to be decoded and the colour is
// reinforcement instead of the only carrier. Order is worst first.
const SEVERITY_GROUPS: {
  severity: OpportunitySeverity
  labelKey: keyof typeof LEADS_PANE_T & string
  heading: string
  bar: string
  dot: string
  chip: string
}[] = [
  {
    severity: "high",
    labelKey: "opportunitySeverityHigh",
    heading: "text-destructive",
    bar: "border-l-destructive",
    dot: "bg-destructive",
    chip: "bg-destructive/10 text-destructive",
  },
  {
    severity: "medium",
    labelKey: "opportunitySeverityMedium",
    heading: "text-warning-text",
    bar: "border-l-warning",
    dot: "bg-warning",
    chip: "bg-warning/15 text-warning-text",
  },
  {
    severity: "positive",
    labelKey: "opportunitySeverityPositive",
    heading: "text-muted-foreground",
    bar: "border-l-muted-foreground",
    dot: "bg-muted-foreground",
    chip: "bg-muted text-foreground",
  },
]

// The step that holds buying signals. They answer "can they pay", not "what is
// wrong with the site", so the audit shows them beside the problems instead of
// as one more group in the rail.
const BUYING_SIGNALS_SEVERITY: OpportunitySeverity = "positive"
const PROBLEM_GROUPS = SEVERITY_GROUPS.filter(
  (group) => group.severity !== BUYING_SIGNALS_SEVERITY,
)

// Whether customers find the business at all is the heart of the pitch, so the
// three visibility axes lead the rail in this fixed order, whatever their
// severity. Every other theme follows under the site section, worst first.
const VISIBILITY_THEMES: LeadsOpportunityTheme[] = [
  "visibility_keyword",
  "visibility_geo",
  "visibility_ai",
]

// A report stored before a severity rename can carry a value no group claims;
// folding it into the middle step keeps the theme on the card.
export function severityGroup(severity: OpportunitySeverity) {
  return (
    SEVERITY_GROUPS.find((group) => group.severity === severity) ??
    SEVERITY_GROUPS[1]
  )
}

// The site's own placement on the campaign keyword, as a one-row table in
// the shared layout (header hairline, flat row, right-aligned verdict). Fed by
// the report's `rank`: the query, the results page and the absolute position.
// Deliberately uncoloured: the theme's colour is already the verdict on it.
// One row, so no sort control.
function RankTable({
  rank,
  tr,
}: {
  rank: LeadsOpportunityRank
  tr: LeadsPaneTr
}) {
  // Closed by the hairline that splits the findings, which follow it.
  return (
    <div className="border-border/70 border-b pb-2 text-xs">
      <p className="text-foreground font-medium">
        {tr("opportunityChecksLabel")}
      </p>
      <div
        className={cn(
          "text-muted-foreground border-border/70 text-2xs mt-1 grid items-center gap-x-2 border-b px-1 pb-1 font-medium",
          RANK_TABLE_COLUMNS,
        )}
      >
        <span className="min-w-0 truncate">
          {tr("opportunityKeywordSearchHeader")}
        </span>
        <span className="text-right">{tr("opportunityRankPageHeader")}</span>
        <span className="text-right">
          {tr("opportunityKeywordPositionHeader")}
        </span>
      </div>
      <div
        className={cn(
          "mt-0.5 grid items-center gap-x-2 px-1 py-1",
          RANK_TABLE_COLUMNS,
        )}
      >
        <span className="text-foreground min-w-0 truncate" title={rank.query}>
          {rank.query}
        </span>
        <span className="text-muted-foreground text-right tabular-nums">
          {rank.page}
        </span>
        <span className="text-foreground text-right font-medium tabular-nums">
          {tr("opportunityRankShort").replace("{n}", String(rank.absolute))}
        </span>
      </div>
      <p className="text-muted-foreground mt-1 px-1">
        {tr("opportunityRankHint")}
      </p>
    </div>
  )
}

// The measured local searches (the site's trade in its own town and the next
// ones), in the shared table layout: query, results page, position or "not
// listed" in the danger token. Sortable on position, best first, the searches
// the site is missing from always last. Fed by an item's `checks`.
function RankChecksTable({
  rows,
  tr,
}: {
  rows: LeadsRankCheck[]
  tr: LeadsPaneTr
}) {
  const [descending, setDescending] = useState(false)
  const sort = { key: "position" as const, descending }
  const sorted = [...rows].sort((a, b) => {
    if (a.found !== b.found) return a.found ? -1 : 1
    if (!a.found) return 0
    const sign = descending ? -1 : 1
    return (
      sign *
      ((a.absolute ?? Number.MAX_SAFE_INTEGER) -
        (b.absolute ?? Number.MAX_SAFE_INTEGER))
    )
  })
  return (
    <div className="text-xs">
      <div
        className={cn(
          "text-muted-foreground border-border/70 text-2xs mt-1 grid items-center gap-x-2 border-b px-1 pb-1 font-medium",
          RANK_TABLE_COLUMNS,
        )}
      >
        <span className="min-w-0 truncate">
          {tr("opportunityKeywordSearchHeader")}
        </span>
        <span className="text-right">{tr("opportunityRankPageHeader")}</span>
        <KeywordSortHeader
          label={tr("opportunityKeywordPositionHeader")}
          sortKey="position"
          sort={sort}
          onSort={() => setDescending((current) => !current)}
          className="justify-self-end text-right"
        />
      </div>
      <ul className="mt-0.5 flex flex-col">
        {sorted.map((row) => (
          <li
            key={row.query}
            className={cn(
              "grid items-center gap-x-2 px-1 py-1",
              RANK_TABLE_COLUMNS,
            )}
          >
            <span className="flex min-w-0 items-baseline gap-1.5">
              <span className="text-foreground truncate" title={row.query}>
                {row.query}
              </span>
              {row.base && (
                <span className="bg-muted text-muted-foreground text-2xs shrink-0 rounded-sm px-1">
                  {tr("opportunityLocalBase")}
                </span>
              )}
            </span>
            <span className="text-muted-foreground text-right tabular-nums">
              {row.found ? row.page : null}
            </span>
            <span
              className={cn(
                "text-right font-medium tabular-nums",
                row.found ? "text-foreground" : "text-destructive",
              )}
            >
              {row.found
                ? tr("opportunityRankShort").replace(
                    "{n}",
                    String(row.absolute),
                  )
                : tr("opportunityRankCheckAbsent")}
            </span>
          </li>
        ))}
      </ul>
      <p className="text-muted-foreground mt-1 px-1">
        {tr("opportunityChecksHint")}
      </p>
    </div>
  )
}

// The rank table's grid: query, results page, position.
const RANK_TABLE_COLUMNS = "grid-cols-[minmax(0,1fr)_3rem_4.5rem]"

// Three states, not two: an absent `found` means the SERP walk has not landed
// yet, and printing "non listé" there would publish an absence nobody checked.
function keywordPositionLabel(
  row: LeadsKeywordOpportunity,
  tr: LeadsPaneTr,
): string {
  if (row.found === undefined) return tr("opportunityKeywordPositionUnknown")
  if (!row.found) return tr("opportunityRankCheckAbsent")
  return tr("opportunityRankShort").replace("{n}", String(row.absolute))
}

// Colour carries the verdict so 20 rows can be triaged without reading numbers:
// red is the work to sell, amber is off page one. A search already won is muted
// rather than green: it is not an opportunity, and green next to the red read as
// a score of the site. Faint grey means nobody has looked yet.
function keywordPositionTone(row: LeadsKeywordOpportunity): string {
  if (row.found === undefined) return "text-muted-foreground/60"
  if (!row.found) return "text-destructive"
  const absolute = row.absolute ?? 0
  if (absolute <= KEYWORD_RANK_TOP) return "text-muted-foreground"
  if (absolute <= KEYWORD_RANK_PAGE_ONE) return "text-foreground"
  return "text-warning-text"
}

// One search worth winning: the wording, a bar sizing its traffic against the
// busiest row, and the placement. The bar is what makes the table scannable at
// twenty rows, where bare numbers force the reader to compare digit by digit.
// The reason the agent flagged it opens on click, because these lists are
// scanned and a hover would flash panel after panel.
function KeywordPerformanceRow({
  row,
  maxVolume,
  tr,
}: {
  row: LeadsKeywordOpportunity
  maxVolume: number
  tr: LeadsPaneTr
}) {
  const position = keywordPositionLabel(row, tr)
  const summary = [
    tr("opportunityKeywordVolumeSummary").replace(
      "{n}",
      String(row.search_volume),
    ),
    position,
  ].join(" · ")
  const share =
    maxVolume > 0 ? Math.max(2, (row.search_volume / maxVolume) * 100) : 0
  return (
    <li>
      <Popover>
        <PopoverTrigger asChild>
          <button
            type="button"
            className="hover:bg-accent text-2xs grid w-full grid-cols-[minmax(0,1fr)_2.5rem_3.5rem_4rem] items-center gap-x-2 rounded px-1 py-1 text-left transition-colors"
          >
            <span className="min-w-0 truncate">{row.keyword}</span>
            <span className="text-muted-foreground shrink-0 text-right tabular-nums">
              {row.search_volume}
            </span>
            <span className="bg-muted h-1 shrink-0 overflow-hidden rounded-full">
              <span
                className="bg-muted-foreground/40 block h-full rounded-full"
                style={{ width: `${share}%` }}
              />
            </span>
            <span
              className={cn(
                "shrink-0 text-right font-medium tabular-nums",
                keywordPositionTone(row),
              )}
            >
              {position}
            </span>
          </button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-72 space-y-1.5 text-xs">
          <p className="font-medium break-words">{row.keyword}</p>
          <p className="text-muted-foreground font-medium">{summary}</p>
          {row.reason && (
            <div className="space-y-0.5">
              <p className="text-muted-foreground">
                {tr("opportunityKeywordReasonHint")}
              </p>
              <p className="text-muted-foreground leading-snug">{row.reason}</p>
            </div>
          )}
        </PopoverContent>
      </Popover>
    </li>
  )
}

type KeywordSortKey = "volume" | "position"
type KeywordSort = { key: KeywordSortKey; descending: boolean }

// Best placement first: a ranked search by its absolute rank, then the ones
// checked and not found, then the ones nobody has checked yet.
function keywordRankValue(row: LeadsKeywordOpportunity): number {
  if (row.found === undefined) return Number.MAX_SAFE_INTEGER
  if (!row.found) return Number.MAX_SAFE_INTEGER - 1
  return row.absolute ?? Number.MAX_SAFE_INTEGER - 1
}

function sortKeywordRows(
  rows: LeadsKeywordOpportunity[],
  sort: KeywordSort,
): LeadsKeywordOpportunity[] {
  const sign = sort.descending ? -1 : 1
  return [...rows].sort((a, b) => {
    const primary =
      sort.key === "volume"
        ? a.search_volume - b.search_volume
        : keywordRankValue(a) - keywordRankValue(b)
    // Ties fall back to the busiest search, whatever the direction.
    return sign * primary || b.search_volume - a.search_volume
  })
}

// A sortable column header, shared by the keyword and the AI question tables.
function KeywordSortHeader<K extends string>({
  label,
  sortKey,
  sort,
  onSort,
  className,
}: {
  label: string
  sortKey: K
  sort: { key: K; descending: boolean }
  onSort: (key: K) => void
  className?: string
}) {
  const active = sort.key === sortKey
  const Icon = active && !sort.descending ? ChevronUp : ChevronDown
  return (
    <button
      type="button"
      onClick={() => onSort(sortKey)}
      aria-sort={
        active ? (sort.descending ? "descending" : "ascending") : undefined
      }
      className={cn(
        "hover:text-foreground focus-visible:ring-ring inline-flex cursor-pointer items-center gap-0.5 rounded transition-colors focus-visible:ring-1 focus-visible:outline-none",
        active && "text-foreground",
        className,
      )}
    >
      {label}
      <Icon className={cn("size-3 shrink-0", !active && "opacity-40")} />
    </button>
  )
}

// Volume and placement have to be read together, which is why this is a table
// and not a badge grid: a chip truncated to half a line cannot hold two numbers
// and still spell the search out.
function KeywordPerformanceList({
  rows,
  tr,
}: {
  rows: LeadsKeywordOpportunity[]
  tr: LeadsPaneTr
}) {
  // Busiest search first by default: it is the one worth selling.
  const [sort, setSort] = useState<KeywordSort>({
    key: "volume",
    descending: true,
  })
  const onSort = (key: KeywordSortKey) =>
    setSort((current) =>
      current.key === key
        ? { key, descending: !current.descending }
        : { key, descending: key === "volume" },
    )
  const sorted = sortKeywordRows(rows, sort)
  // The bar scale is the busiest row, whichever order the table is in.
  const maxVolume = Math.max(0, ...rows.map((row) => row.search_volume))
  // No section label above the column headers: the item title already names
  // what the table is, so a label would say "recherches" twice in a row.
  return (
    <>
      <div className="text-muted-foreground border-border/70 text-2xs mt-1 grid grid-cols-[minmax(0,1fr)_2.5rem_3.5rem_4rem] items-center gap-x-2 border-b px-1 pb-1 font-medium">
        <span className="min-w-0 truncate">
          {tr("opportunityKeywordSearchHeader")}
        </span>
        <KeywordSortHeader
          label={tr("opportunityKeywordVolumeHeader")}
          sortKey="volume"
          sort={sort}
          onSort={onSort}
          className="col-span-2 justify-self-start text-left"
        />
        <KeywordSortHeader
          label={tr("opportunityKeywordPositionHeader")}
          sortKey="position"
          sort={sort}
          onSort={onSort}
          className="justify-self-end text-right"
        />
      </div>
      <ul className="mt-0.5 flex flex-col">
        {sorted.map((row) => (
          <KeywordPerformanceRow
            key={row.keyword}
            row={row}
            maxVolume={maxVolume}
            tr={tr}
          />
        ))}
      </ul>
    </>
  )
}

// One question put to an AI, opening on the answer it gave: the question as the
// title, whether the business was cited and where, then the sites the answer
// cited in order, the business's own row marked. `position` is the business's
// index in `sources` plus one (0 when absent), see services/geo_check.py.
function GeoQuestionBadge({
  question,
  category,
  tr,
}: {
  question: LeadsGeoQuestion
  category: string
  tr: LeadsPaneTr
}) {
  // A BCP 47 tag, which the plural rule of `fill` expects.
  const pluralLocale = useDateLocale()
  const ownIndex = question.cited ? question.position - 1 : -1
  // Capped, but never above the business's own row: that row is the point.
  const visibleCount = Math.max(GEO_SOURCES_MAX_VISIBLE, ownIndex + 1)
  const visible = question.sources.slice(0, visibleCount)
  const hidden = question.sources.length - visible.length
  return (
    <li>
      <Popover>
        {/* A row of the questions table, laid out like the keyword table:
            the question, how many sites the answer cited, the business's
            position in them. The click opens the answer. */}
        <PopoverTrigger asChild>
          <button
            type="button"
            className={cn(
              "hover:bg-accent focus-visible:ring-ring grid w-full items-center gap-x-2 rounded px-1 py-1 text-left text-xs transition-colors outline-none focus-visible:ring-2",
              GEO_TABLE_COLUMNS,
            )}
          >
            <span className="text-foreground min-w-0 truncate">
              {question.question}
            </span>
            <span className="text-muted-foreground text-right tabular-nums">
              {question.sources.length}
            </span>
            <span
              className={cn(
                "text-right font-medium tabular-nums",
                question.cited ? "text-foreground" : "text-destructive",
              )}
            >
              {question.cited
                ? `#${question.position}`
                : tr("opportunityGeoAbsent")}
            </span>
          </button>
        </PopoverTrigger>
        <PopoverContent
          align="start"
          className="w-80 space-y-3 text-xs sm:w-96"
        >
          <div className="space-y-1">
            <p className="text-muted-foreground">{category}</p>
            <p className="text-foreground text-2sm leading-snug font-medium break-words">
              {question.question}
            </p>
            <p
              className={cn(
                "font-medium",
                question.cited ? "text-foreground" : "text-destructive",
              )}
            >
              {question.cited
                ? fill(tr("opportunityGeoCitedAt"), { n: question.position })
                : tr("opportunityGeoAbsentStatus")}
            </p>
          </div>
          {visible.length > 0 && (
            <div className="border-border/70 space-y-1.5 border-t pt-2.5">
              <p className="text-muted-foreground">
                {tr("opportunityGeoSourcesLabel")}
              </p>
              <ol className="space-y-0.5">
                {visible.map((source, index) => {
                  const own = index === ownIndex
                  return (
                    <li
                      key={`${source}-${index}`}
                      className="flex min-w-0 items-baseline gap-2"
                    >
                      <span className="text-muted-foreground w-4 shrink-0 text-right tabular-nums">
                        {index + 1}.
                      </span>
                      <span
                        className={cn(
                          "min-w-0 truncate",
                          own ? "text-primary font-medium" : "text-foreground",
                        )}
                        title={source}
                      >
                        {source}
                      </span>
                      {own && (
                        <span className="bg-muted text-muted-foreground text-2xs shrink-0 rounded-sm px-1">
                          {tr("opportunityGeoOwnSite")}
                        </span>
                      )}
                    </li>
                  )
                })}
              </ol>
              {hidden > 0 && (
                <p className="text-muted-foreground pl-6">
                  {fill(
                    tr("opportunityGeoMoreSources"),
                    { n: hidden },
                    pluralLocale,
                  )}
                </p>
              )}
            </div>
          )}
        </PopoverContent>
      </Popover>
    </li>
  )
}

// The AI questions table's grid: question, sites cited, position.
const GEO_TABLE_COLUMNS = "grid-cols-[minmax(0,1fr)_4.5rem_4.5rem]"

type GeoSortKey = "sources" | "position"
type GeoSort = { key: GeoSortKey; descending: boolean }

// Best placement first; a question where the business is absent comes after
// every cited one. Ties fall back to the question with the most sites cited.
function sortGeoQuestions(
  rows: LeadsGeoQuestion[],
  sort: GeoSort,
): LeadsGeoQuestion[] {
  const sign = sort.descending ? -1 : 1
  const rank = (row: LeadsGeoQuestion) =>
    row.cited ? row.position : Number.MAX_SAFE_INTEGER
  return [...rows].sort((a, b) => {
    const primary =
      sort.key === "sources"
        ? a.sources.length - b.sources.length
        : rank(a) - rank(b)
    return sign * primary || b.sources.length - a.sources.length
  })
}

// The questions put to an AI, as a table like the keyword one, split by what
// they are for: what a customer of the trade asks before choosing, and what
// "doing it well" means. Grouped because the two answer different objections
// in a sales call; one header and one sort for both groups.
function GeoQuestionList({
  questions,
  tr,
}: {
  questions: LeadsGeoQuestion[]
  tr: LeadsPaneTr
}) {
  // Best placement first by default: where the business already shows up.
  const [sort, setSort] = useState<GeoSort>({
    key: "position",
    descending: false,
  })
  const onSort = (key: GeoSortKey) =>
    setSort((current) =>
      current.key === key
        ? { key, descending: !current.descending }
        : { key, descending: key === "sources" },
    )
  const groups = [
    { kind: "buyer", labelKey: "opportunityGeoBuyerQuestions" },
    { kind: "best_practice", labelKey: "opportunityGeoPracticeQuestions" },
  ] as const
  return (
    <>
      <div
        className={cn(
          "text-muted-foreground border-border/70 text-2xs mt-1 grid items-center gap-x-2 border-b px-1 pb-1 font-medium",
          GEO_TABLE_COLUMNS,
        )}
      >
        <span className="min-w-0 truncate">
          {tr("opportunityGeoQuestionHeader")}
        </span>
        <KeywordSortHeader
          label={tr("opportunityGeoSourcesHeader")}
          sortKey="sources"
          sort={sort}
          onSort={onSort}
          className="justify-self-end text-right"
        />
        <KeywordSortHeader
          label={tr("opportunityKeywordPositionHeader")}
          sortKey="position"
          sort={sort}
          onSort={onSort}
          className="justify-self-end text-right"
        />
      </div>
      <ul className="mt-0.5 flex flex-col">
        {groups.map(({ kind, labelKey }) => {
          const rows = questions.filter((q) => q.kind === kind)
          if (rows.length === 0) return null
          return [
            <li
              key={kind}
              className="text-muted-foreground px-1 pt-2 pb-0.5 text-xs first:pt-1"
            >
              {tr(labelKey)}
            </li>,
            ...sortGeoQuestions(rows, sort).map((question) => (
              <GeoQuestionBadge
                key={question.question}
                question={question}
                category={tr(labelKey)}
                tr={tr}
              />
            )),
          ]
        })}
      </ul>
    </>
  )
}

// The row's source, when it has one the user can open: the audited Google
// profile, or the Lighthouse report behind the speed numbers. PageSpeed re-runs
// on open, so its figures can differ slightly from the stored ones.
function themeSourceUrl(
  key: LeadsOpportunityTheme,
  report: LeadsOpportunityReportType,
  website?: string,
  mapsUrl?: string,
): string | undefined {
  if (key === "google_profile")
    return report.profile_url || mapsUrl || undefined
  if (key === "speed" && website)
    return `${PAGESPEED_REPORT_URL}?url=${encodeURIComponent(website)}&form_factor=mobile`
  return undefined
}

// One rail entry per theme: severity dot, name, how many findings it holds.
// A row in the side rail from sm up, a chip in a horizontal row below it.
function ThemeRailRow({
  theme,
  active,
  onSelect,
  tr,
  chip = false,
}: {
  theme: LeadsOpportunityReportType["themes"][number]
  active: boolean
  onSelect: () => void
  tr: LeadsPaneTr
  chip?: boolean
}) {
  const labelKey = OPPORTUNITY_THEME_LABELS[theme.key]
  return (
    <li className={cn(chip && "shrink-0")}>
      <button
        type="button"
        onClick={onSelect}
        aria-current={active}
        className={cn(
          "focus-visible:ring-ring flex items-center text-left text-xs transition-colors outline-none focus-visible:ring-2",
          chip
            ? "gap-1.5 rounded-full border px-2.5 py-1 whitespace-nowrap"
            : "w-full gap-2 rounded-md border border-transparent px-1.5 py-1",
          chip &&
            (active
              ? "border-foreground/20 bg-accent text-foreground font-medium"
              : "border-border text-muted-foreground hover:bg-accent/50"),
          !chip &&
            (active
              ? "border-border/70 bg-background font-medium shadow-xs"
              : "hover:bg-accent/50"),
        )}
      >
        <span
          aria-hidden
          className={cn(
            "size-1.5 shrink-0 rounded-full",
            severityGroup(theme.severity).dot,
          )}
        />
        <span className={cn(!chip && "min-w-0 flex-1 truncate")}>
          {labelKey ? tr(labelKey) : theme.key}
        </span>
        <span className="text-muted-foreground shrink-0 tabular-nums">
          {theme.items.length}
        </span>
      </button>
    </li>
  )
}

// A category's header, as the audit drew it before: an icon tile and a pill
// tinted by severity, the name, and how many signals it holds, closed by the
// same hairline that splits the findings. The pane and the buying signals
// column share it.
function ThemeHeader({
  theme,
  tr,
}: {
  theme: LeadsOpportunityReportType["themes"][number]
  tr: LeadsPaneTr
}) {
  const pluralLocale = useDateLocale()
  // A report stored before the theme rename can carry an unknown key; falling
  // back keeps the card rendering until the backfill has run.
  const Icon = OPPORTUNITY_THEME_ICONS[theme.key] ?? Gauge
  const labelKey = OPPORTUNITY_THEME_LABELS[theme.key]
  const group = severityGroup(theme.severity)
  return (
    <div className="border-border/70 flex items-start gap-2 border-b pb-3 text-xs">
      <span
        className={cn(
          "flex size-8 shrink-0 items-center justify-center rounded-md",
          group.chip,
        )}
      >
        <Icon className="size-4" aria-hidden />
      </span>
      <div className="min-w-0 flex-1">
        <p className="text-foreground text-2sm truncate font-medium">
          {labelKey ? tr(labelKey) : theme.key}
        </p>
        <p className="text-muted-foreground truncate">
          {fill(
            tr("opportunityThemeSignalsCount"),
            { n: theme.items.length },
            pluralLocale,
          )}
        </p>
      </div>
      <span
        className={cn(
          "shrink-0 rounded-full px-2 py-0.5 font-medium",
          group.chip,
        )}
      >
        {tr(group.labelKey)}
      </span>
    </div>
  )
}

// The detail pane: the category header, then the theme's findings as flat rows split by hairlines, each
// with its evidence under it. The backend already wrote the copy in the
// campaign team's language.
function ThemeDetail({
  theme,
  rank,
  sourceUrl,
  tr,
}: {
  theme: LeadsOpportunityReportType["themes"][number]
  rank?: LeadsOpportunityRank
  sourceUrl?: string
  tr: LeadsPaneTr
}) {
  return (
    <div className="flex flex-col gap-2">
      <ThemeHeader theme={theme} tr={tr} />
      {/* The checks table's base row already carries this placement. */}
      {rank && !theme.items.some((item) => item.checks?.length) && (
        <RankTable rank={rank} tr={tr} />
      )}
      {sourceUrl && (
        <a
          href={isHttpUrl(sourceUrl) ? sourceUrl : undefined}
          target="_blank"
          rel="noopener noreferrer"
          className="text-muted-foreground hover:text-foreground inline-flex items-center gap-1 self-start text-xs underline underline-offset-2"
        >
          <ExternalLink className="size-3 shrink-0" />
          {tr(
            theme.key === "speed"
              ? "opportunitySpeedLink"
              : "opportunityProfileLink",
          )}
        </a>
      )}
      <ul className="divide-border/70 flex flex-col divide-y">
        {theme.items.map((item) => {
          const hasEvidence = Boolean(
            item.keywords?.length ||
            item.questions?.length ||
            item.checks?.length,
          )
          // The AI questions' own share, drawn under their summary title: the
          // bar says "6 of 8" at a glance, so the restating detail line goes.
          const buyerQuestions = (item.questions ?? []).filter(
            (question) => question.kind === "buyer",
          )
          const citedShare =
            buyerQuestions.length > 0
              ? (buyerQuestions.filter((question) => question.cited).length /
                  buyerQuestions.length) *
                100
              : null
          return (
            <li key={item.code} className="py-2 text-xs first:pt-0 last:pb-0">
              <p className="text-foreground font-medium">{item.title}</p>
              {citedShare !== null && (
                <div className="bg-muted mt-1 h-1 max-w-48 overflow-hidden rounded-full">
                  <div
                    className="bg-foreground/40 h-full rounded-full"
                    style={{ width: `${citedShare}%` }}
                  />
                </div>
              )}
              {item.competitors && item.competitors.length > 0 ? (
                // Each competitor is named, and links to its site when it has
                // one, so a rep can say who the assistants send buyers to.
                <p className="text-muted-foreground leading-snug break-words">
                  {tr("opportunityCompetitorsLead")}{" "}
                  {item.competitors.map(({ name, domain }, index) => (
                    <span key={domain || name}>
                      {index > 0 && ", "}
                      {domain ? (
                        <a
                          href={`https://${domain}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-foreground underline-offset-2 hover:underline"
                        >
                          {name}
                        </a>
                      ) : (
                        name
                      )}
                    </span>
                  ))}
                </p>
              ) : (
                item.detail &&
                citedShare === null && (
                  <p className="text-muted-foreground leading-snug">
                    {item.detail}
                  </p>
                )
              )}
              {hasEvidence && (
                <div className="mt-2">
                  {item.keywords && item.keywords.length > 0 && (
                    <KeywordPerformanceList tr={tr} rows={item.keywords} />
                  )}
                  {item.questions && item.questions.length > 0 && (
                    <GeoQuestionList questions={item.questions} tr={tr} />
                  )}
                  {item.checks && item.checks.length > 0 && (
                    <RankChecksTable rows={item.checks} tr={tr} />
                  )}
                  {item.objections && item.objections.length > 0 && (
                    <>
                      <p className="text-muted-foreground mt-2">
                        {tr("opportunityPitchObjections")}
                      </p>
                      <ul className="mt-0.5 max-w-md space-y-0.5">
                        {item.objections.map((objection) => (
                          <li
                            key={objection}
                            className="text-muted-foreground flex gap-1.5 text-xs"
                          >
                            <span aria-hidden>&bull;</span>
                            <span className="min-w-0 flex-1">{objection}</span>
                          </li>
                        ))}
                      </ul>
                    </>
                  )}
                </div>
              )}
            </li>
          )
        })}
      </ul>
    </div>
  )
}

// One row of the lead card's accordion: every collapsible block of the card
// shares this header, so the rows read as one list (chevron, title, muted
// meta, right-aligned summary) instead of a stack of boxed cards. `actions`
// sit beside the trigger, not inside it: they act without unfolding the row,
// and a button may not nest in the trigger button. The content mounts only
// while open, so a closed row costs nothing to render.
function LeadSection({
  value,
  title,
  meta,
  aside,
  wrapAside = false,
  actions,
  children,
}: {
  value: string
  title: ReactNode
  meta?: ReactNode
  aside?: ReactNode
  // A wide aside (a row of chips) wraps under itself on a narrow card instead
  // of running past the card's edge.
  wrapAside?: boolean
  actions?: ReactNode
  children: ReactNode
}) {
  return (
    <AccordionItem value={value} divider="soft">
      <AccordionPrimitive.Header className="hover:bg-accent/40 flex items-center gap-2 pr-3 text-xs transition-colors">
        <AccordionPrimitive.Trigger className="group/section focus-visible:ring-ring flex min-h-9 min-w-0 flex-1 items-center gap-1.5 rounded-md py-2 pl-3 text-left outline-none focus-visible:ring-2 focus-visible:ring-inset">
          <ChevronRight className="text-muted-foreground size-3.5 shrink-0 transition-transform duration-200 group-data-[state=open]/section:rotate-90 motion-reduce:transition-none" />
          {/* Section level: the card title's weight one size below it, and
              a step above the regular-weight items it opens. Same treatment
              as the Contacts / Phone numbers headings. */}
          <span className="text-foreground text-2sm shrink-0 font-medium">
            {title}
          </span>
          {meta != null && (
            <span className="text-muted-foreground min-w-0 truncate">
              · {meta}
            </span>
          )}
          {aside != null && (
            <span
              className={cn(
                "text-muted-foreground ml-auto flex items-center gap-1.5 pl-2",
                wrapAside ? "min-w-0 justify-end" : "shrink-0",
              )}
            >
              {aside}
            </span>
          )}
        </AccordionPrimitive.Trigger>
        {actions}
      </AccordionPrimitive.Header>
      <AccordionPrimitive.Content className="motion-safe:data-[state=closed]:animate-accordion-up motion-safe:data-[state=open]:animate-accordion-down overflow-hidden">
        {/* Inset by the chevron and its gap, so the content starts under
            the title text, and clear of the hovered header's background.
            Same type scale as the contacts section. */}
        <div className="pt-2 pr-3 pb-3 pl-8 text-xs">{children}</div>
      </AccordionPrimitive.Content>
    </AccordionItem>
  )
}

// The verdict pill of the custom question: a yes reads as good news, a no
// stays a quiet neutral (an answer, not an error). An unclear answer shows
// no pill: the answers below speak for themselves.
const CUSTOM_VERDICT_PILL: Partial<
  Record<
    CustomQueryVerdict,
    {
      label: "customVerdictYes" | "customVerdictNo"
      variant: "success-soft" | "outline"
      chart?: "neutral"
    }
  >
> = {
  yes: { label: "customVerdictYes", variant: "success-soft" },
  no: { label: "customVerdictNo", variant: "outline", chart: "neutral" },
}

// The campaign's own question, answered for this lead: titled with its short
// name, the verdict as a pill on the row (hidden on public pages), and once
// open every answer in full with its notes and source, under the question.
// Nothing until the question has an answer.
function CustomQuerySection({
  lead,
  withVerdict,
  tr,
}: {
  lead: Lead
  withVerdict: boolean
  tr: LeadsPaneTr
}) {
  const verdict = customQueryVerdict(lead)
  if (verdict === null) return null
  const question = lead.custom_query?.text ?? ""
  // The question's short name, else a fixed default: the question itself
  // reads in full inside the section.
  const title = lead.custom_query?.label?.trim() || tr("customAnswerLabel")
  const pill = CUSTOM_VERDICT_PILL[verdict]
  const entries = lead.custom_enrichment.filter((entry) => entry.value.trim())
  return (
    <LeadSection
      value="custom-query"
      title={title}
      aside={
        <>
          {withVerdict && pill && (
            <Badge
              variant={pill.variant}
              chart={pill.chart}
              className="text-2xs px-1.5 py-0"
            >
              {tr(pill.label)}
            </Badge>
          )}
          <Sparkles className="size-3.5" />
        </>
      }
    >
      <div className="space-y-3">
        {/* The question is context, not content: a soft tinted block above
            the answers, quieter than them, in full (the section is opened to
            read it, unlike the chip popover's clamped preview). */}
        {question && (
          <div className="bg-muted/50 text-muted-foreground space-y-1 rounded-md px-3 py-2">
            <p className="text-3xs font-medium tracking-wide uppercase">
              {tr("customQueryHint")}
            </p>
            <p className="leading-relaxed break-words whitespace-pre-wrap">
              {question}
            </p>
          </div>
        )}
        <ul className="divide-border/60 divide-y">
          {entries.map((entry, index) => (
            <CustomQueryFinding
              key={`${entry.value}-${index}`}
              entry={entry}
              tr={tr}
            />
          ))}
        </ul>
      </div>
    </LeadSection>
  )
}

// One answer to the custom question: the verdict mark leads so a column of
// yes and no reads at a glance, then the short answer itself, the source
// closing the row as an icon button. The answer is the whole content: older
// findings may still carry `notes`, but nothing beside the answer shows.
function CustomQueryFinding({
  entry,
  tr,
}: {
  entry: CustomEnrichment
  tr: LeadsPaneTr
}) {
  const verdict = entryVerdict(entry)
  const VerdictIcon = verdict === "yes" ? Check : Minus
  return (
    <li className="group/finding flex items-start gap-2 py-2">
      <VerdictIcon
        aria-hidden
        className={cn(
          "mt-0.5 size-3.5 shrink-0",
          verdict === "yes" ? "text-success" : "text-muted-foreground/60",
        )}
      />
      <p className="text-foreground min-w-0 flex-1 leading-relaxed break-words">
        {customQueryStatement(entry.value)}
      </p>
      {isHttpUrl(entry.source_url) && (
        <a
          href={entry.source_url}
          target="_blank"
          rel="noopener noreferrer"
          title={entry.source_url}
          aria-label={tr("customQuerySource")}
          className="text-muted-foreground hover:bg-accent hover:text-foreground focus-visible:ring-ring -mt-0.5 inline-flex size-6 shrink-0 items-center justify-center rounded-md opacity-60 transition outline-none group-hover/finding:opacity-100 focus-visible:opacity-100 focus-visible:ring-2"
        >
          <ExternalLink className="size-3.5" />
        </a>
      )}
    </li>
  )
}

// A block of the accordion with nothing to unfold (a one-line verdict): same
// row rhythm, no chevron.
function LeadSectionNote({ children }: { children: ReactNode }) {
  return (
    <div className="border-border/70 text-muted-foreground border-b px-3 py-2 text-xs last:border-b-0">
      {children}
    </div>
  )
}

function OpportunityCoverage({
  coverage,
  tr,
}: {
  coverage: LeadsOpportunityCoverage
  tr: LeadsPaneTr
}) {
  if (coverage.blocked.length === 0) return null
  return (
    <div className="text-muted-foreground mt-1.5 space-y-0.5 border-t pt-1.5 text-xs">
      <p>{tr("opportunityBlocked")}</p>
    </div>
  )
}

// Master-detail: the site's problems in a rail on the left (a chip row on a
// phone) with the selected theme's findings beside, and the buying signals in
// a column of their own on the right (under it below lg). Two questions, one place each: what is wrong with the site, and
// whether this business can pay to fix it.
function OpportunityReport({
  report,
  website,
  mapsUrl,
  leadId,
  leadName = "",
  tr,
  defaultOpen = false,
}: {
  report: LeadsOpportunityReportType
  website?: string
  mapsUrl?: string
  leadId?: number
  // Names the PDF dialog and the downloaded file.
  leadName?: string
  tr: LeadsPaneTr
  // The public showcase opens it: there the audit is the point, not a detail,
  // and the showcase frames and toggles it itself, so no accordion row.
  defaultOpen?: boolean
}) {
  // A clean site still has a placement worth showing, so the rank alone is
  // enough to render the card.
  if (report.count === 0 && !report.rank) return null
  const body = (
    <OpportunityReportBody
      report={report}
      website={website}
      mapsUrl={mapsUrl}
      tr={tr}
    />
  )
  if (defaultOpen) return <div className="mt-2">{body}</div>
  // Collapsed by default: the audit is the longest block on the card, and a rep
  // scanning a list of leads wants the header count first and the themes only
  // once they have picked a lead.
  return (
    <LeadSection
      value="audit"
      title={tr("opportunityTitle")}
      // Two numbers, because they are two things: the rail rows are themes,
      // and each theme holds several findings.
      meta={
        <>
          {(report.themes.length > 1
            ? tr("opportunityThemeCount")
            : tr("opportunityThemeCountOne")
          ).replace("{n}", String(report.themes.length))}
          {", "}
          {(report.count > 1
            ? tr("opportunitySignalCount")
            : tr("opportunitySignalCountOne")
          ).replace("{n}", String(report.count))}
        </>
      }
      // Beside the trigger, not inside it: the PDF is asked for without
      // unfolding the report.
      actions={
        leadId !== undefined && report.count > 0 ? (
          <LeadAuditPdfButton leadId={leadId} leadName={leadName} />
        ) : undefined
      }
    >
      {body}
    </LeadSection>
  )
}

// The buying signals beside the audit: what shows this prospect can pay. An
// intro line, then per theme a header (icon tile, name, how many signals, its
// "can invest" pill) and one bordered card per signal.
function BuyingSignalsColumn({
  themes,
  tr,
}: {
  themes: LeadsOpportunityReportType["themes"]
  tr: LeadsPaneTr
}) {
  return (
    <div className="flex flex-col gap-3 text-xs">
      <p className="text-muted-foreground leading-snug">
        {tr("opportunityBuyingSignalsHint")}
      </p>
      {themes.map((theme) => {
        return (
          <div key={theme.key} className="flex flex-col gap-2">
            <ThemeHeader theme={theme} tr={tr} />
            <ul className="flex flex-col gap-2">
              {theme.items.map((item) => (
                <li
                  key={item.code}
                  className="border-border/70 rounded-md border p-2.5"
                >
                  <p className="text-foreground font-medium">{item.title}</p>
                  {item.detail && (
                    <p className="text-muted-foreground mt-0.5 leading-snug">
                      {item.detail}
                    </p>
                  )}
                </li>
              ))}
            </ul>
          </div>
        )
      })}
    </div>
  )
}

// The audit itself, mounted only once it is shown.
function OpportunityReportBody({
  report,
  website,
  mapsUrl,
  tr,
}: {
  report: LeadsOpportunityReportType
  website?: string
  mapsUrl?: string
  tr: LeadsPaneTr
}) {
  const [selectedKey, setSelectedKey] = useState<string | null>(null)
  // Keyed on the selection: a taller theme has to re-measure without any
  // scrolling having happened.
  const detailHint = useScrollHint(selectedKey ?? "")
  const problems = report.themes.filter(
    (theme) =>
      severityGroup(theme.severity).severity !== BUYING_SIGNALS_SEVERITY,
  )
  const buyingSignals = report.themes.filter(
    (theme) =>
      severityGroup(theme.severity).severity === BUYING_SIGNALS_SEVERITY,
  )
  const severityRank = (theme: (typeof problems)[number]) =>
    PROBLEM_GROUPS.indexOf(severityGroup(theme.severity))
  const sections: {
    labelKey: keyof typeof LEADS_PANE_T & string
    themes: typeof problems
  }[] = [
    {
      labelKey: "opportunitySectionVisibility",
      themes: VISIBILITY_THEMES.flatMap((key) =>
        problems.filter((theme) => theme.key === key),
      ),
    },
    {
      labelKey: "opportunitySectionSite",
      themes: problems
        .filter((theme) => !VISIBILITY_THEMES.includes(theme.key))
        .sort((a, b) => severityRank(a) - severityRank(b)),
    },
  ]
  // The rail opens on the first visibility theme, the core of the pitch. The
  // buying signals answer another question (can they pay), so they keep a
  // column of their own beside it.
  const ordered = sections.flatMap((section) => section.themes)
  const selected =
    ordered.find((theme) => theme.key === selectedKey) ?? ordered[0]
  const railEntry = (theme: (typeof ordered)[number], chip: boolean) => (
    <ThemeRailRow
      key={theme.key}
      theme={theme}
      active={theme.key === selected?.key}
      onSelect={() => setSelectedKey(theme.key)}
      tr={tr}
      chip={chip}
    />
  )
  return (
    <>
      <div
        className={cn(
          "flex flex-col gap-4",
          ordered.length > 0 &&
            buyingSignals.length > 0 &&
            "lg:grid lg:grid-cols-[minmax(0,1fr)_minmax(0,18rem)]",
        )}
      >
        {ordered.length > 0 && (
          // From sm up the pane has a fixed height: switching themes never
          // moves the card, and a theme with twelve findings scrolls inside.
          <div className="flex flex-col gap-3 sm:grid sm:grid-cols-[minmax(0,13rem)_1fr]">
            {/* Below sm the rail is a row of chips: one horizontal strip, and
              the pane flows under it without a scroll box of its own. */}
            <ul className="-mx-1 flex gap-1.5 overflow-x-auto px-1 pb-1 sm:hidden">
              {ordered.map((theme) => railEntry(theme, true))}
            </ul>
            <div className="hidden flex-col gap-3 sm:flex sm:border-r sm:pr-3">
              {sections.map((section) => {
                if (section.themes.length === 0) return null
                return (
                  <div key={section.labelKey}>
                    <p className="text-muted-foreground mb-1 px-1.5 text-xs">
                      {tr(section.labelKey)}
                    </p>
                    <ul className="flex flex-col gap-0.5">
                      {section.themes.map((theme) => railEntry(theme, false))}
                    </ul>
                  </div>
                )
              })}
            </div>
            <div className="relative min-h-0">
              <div
                ref={detailHint.ref}
                // A vertical scroll box clips on X too: it is widened by the
                // rows' hover inset (-mx-1.5) and padded back, so nothing pokes
                // past its edge and it never scrolls sideways.
                className="sm:-ml-1.5 sm:h-80 sm:overflow-x-hidden sm:overflow-y-auto sm:pr-1.5 sm:pl-1.5"
              >
                {selected && (
                  <ThemeDetail
                    theme={selected}
                    rank={
                      selected.key === "visibility_geo"
                        ? report.rank
                        : undefined
                    }
                    sourceUrl={themeSourceUrl(
                      selected.key,
                      report,
                      website,
                      mapsUrl,
                    )}
                    tr={tr}
                  />
                )}
              </div>
              {detailHint.hasMore && <ScrollHintArrow />}
            </div>
          </div>
        )}
        {buyingSignals.length > 0 && (
          <div
            className={cn(
              ordered.length > 0 &&
                "border-border/70 border-t pt-4 lg:border-t-0 lg:border-l lg:pt-0 lg:pl-4",
            )}
          >
            <BuyingSignalsColumn themes={buyingSignals} tr={tr} />
          </div>
        )}
      </div>
      {report.coverage && (
        <OpportunityCoverage coverage={report.coverage} tr={tr} />
      )}
    </>
  )
}

// Job-offers plugin output: a collapsed row that says how many positions are
// open, expanding to one link per posting. Collapsed by default because hiring
// is a yes/no signal first; the titles matter only once a rep opens the lead.
//
// A run that found nothing still renders: expanding it names the sources that
// were checked, so "not hiring" is a verified answer rather than a blank the
// user has to interpret. The card only disappears when the plugin never ran.
function JobOffersList({
  offers,
  checked,
  tr,
}: {
  offers: LeadsJobOffer[]
  checked: LeadsJobOffersChecked
  tr: LeadsPaneTr
}) {
  const [showAll, setShowAll] = useState(false)
  const sources = checked?.sources ?? []
  const ran = sources.length > 0
  if (offers.length === 0 && !ran) return null
  const empty = offers.length === 0
  const visible = showAll ? offers : offers.slice(0, JOB_OFFERS_MAX_VISIBLE)
  const hidden = offers.length - visible.length
  return (
    <LeadSection
      value="jobs"
      title={tr("jobOffersTitle")}
      aside={
        <>
          <span className="tabular-nums">
            {empty
              ? tr("jobOffersNone")
              : (offers.length > 1
                  ? tr("jobOffersCount")
                  : tr("jobOffersCountOne")
                ).replace("{n}", String(offers.length))}
          </span>
          <Briefcase className="size-3.5" />
        </>
      }
    >
      <div>
        {empty ? (
          <p className="text-muted-foreground leading-snug">
            {tr("jobOffersNoneHelp")}
          </p>
        ) : (
          <>
            <ul>
              {visible.map((offer) => (
                <li key={offer.url}>
                  {/* Title first, flush with the section title; the
                      external-link cue trails, as a meta. */}
                  <a
                    href={isHttpUrl(offer.url) ? offer.url : undefined}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hover:bg-accent/50 -mx-1.5 flex items-center gap-2 rounded-md px-1.5 py-1 transition-colors"
                  >
                    <span className="text-foreground min-w-0 flex-1 truncate">
                      {offer.title}
                    </span>
                    <span className="text-muted-foreground shrink-0">
                      {[offer.source, offer.location]
                        .filter(Boolean)
                        .join(" · ")}
                    </span>
                    <ExternalLink className="text-muted-foreground size-3 shrink-0" />
                  </a>
                </li>
              ))}
            </ul>
            {hidden > 0 && (
              <button
                type="button"
                onClick={() => setShowAll(true)}
                className="text-muted-foreground hover:text-foreground hover:bg-accent mt-1 -ml-1.5 rounded-md px-1.5 py-1 font-medium"
              >
                {fill(tr("jobOffersShowMore"), { n: hidden })}
              </button>
            )}
          </>
        )}
        {ran && (
          <div
            className={cn(
              "text-muted-foreground flex flex-wrap items-center gap-1",
              empty ? "mt-1.5" : "mt-2 border-t pt-1.5",
            )}
          >
            <span>{tr("jobOffersSourcesChecked")}</span>
            {sources.map((source) => (
              <span
                key={source}
                className="border-border/60 rounded-sm border px-1 py-0.5"
              >
                {jobOfferSourceLabel(source, tr)}
              </span>
            ))}
          </div>
        )}
      </div>
    </LeadSection>
  )
}

function truncateUrl(url: string): string {
  if (url.length <= URL_MAX_LEN) return url
  return `${url.slice(0, URL_MAX_LEN - 1)}…`
}

// Custom-query plugin results rendered as a compact wrapping row of chips.
// Clicking a chip opens a popover with the notes, source, and the question
// the campaign asked. One chip per stored entry. `maxVisible` caps the row
// behind a "+N more" toggle.
function CustomEnrichmentChips({
  entries,
  question,
  maxVisible,
}: {
  entries: CustomEnrichment[]
  question: string | null
  maxVisible?: number
}) {
  const tr = useTr(LEADS_PANE_T)
  const [showAll, setShowAll] = useState(false)
  if (entries.length === 0) return null
  const visible =
    maxVisible !== undefined && !showAll
      ? entries.slice(0, maxVisible)
      : entries
  const hidden = entries.length - visible.length
  return (
    <div className="mt-2 flex flex-wrap gap-1">
      {visible.map((entry, idx) => (
        <Popover key={idx}>
          <PopoverTrigger asChild>
            <button
              type="button"
              aria-label={tr("customQueryHint")}
              className="border-border hover:bg-accent inline-flex min-w-0 items-start gap-1 rounded-full border px-2 py-0.5 text-left text-xs"
            >
              <Sparkles className="text-primary mt-0.5 size-3 shrink-0" />
              <span className="font-medium break-words whitespace-normal">
                {entry.value}
              </span>
            </button>
          </PopoverTrigger>
          <PopoverContent align="start" className="w-72 space-y-2 text-xs">
            <p className="font-medium break-all">{entry.value}</p>
            {question && (
              <div className="space-y-0.5">
                <p className="text-muted-foreground text-3xs font-medium tracking-wide uppercase">
                  {tr("customQueryHint")}
                </p>
                <ExpandableQuestion
                  question={question}
                  showFullLabel={tr("customQueryShowFull")}
                  showLessLabel={tr("customQueryShowLess")}
                />
              </div>
            )}
            {entry.source_url && (
              <a
                href={
                  isHttpUrl(entry.source_url) ? entry.source_url : undefined
                }
                target="_blank"
                rel="noreferrer"
                className="text-primary inline-flex items-center gap-1 hover:underline"
              >
                <ExternalLink className="size-3" />
                {tr("customQuerySource")}
              </a>
            )}
          </PopoverContent>
        </Popover>
      ))}
      {hidden > 0 && (
        <button
          type="button"
          onClick={() => setShowAll(true)}
          className="text-muted-foreground hover:text-foreground px-1 text-xs"
        >
          {tr("customQueryShowMore").replace("{n}", String(hidden))}
        </button>
      )}
    </div>
  )
}

// Ad platforms carry very different meanings: an active Meta campaign, a
// sponsored Google result, and a paid Pages Jaunes listing all set has_ads, so
// the tooltip names them rather than showing the raw key.
const AD_PLATFORM_LABELS: Record<LeadsAdCheckRegistryKey, string> = {
  meta_ad_library: "Meta Ad Library",
  google_ads: "Google Ads",
  tiktok_ad_library: "TikTok Ad Library",
  pages_jaunes: "Pages Jaunes",
}

function adPlatformLabel(platform: LeadsAdCheckRegistryKey): string {
  return AD_PLATFORM_LABELS[platform] ?? platform
}

// Platform names are two or three words each, so a plain join wraps mid-name and
// reads as two platforms. Non-breaking spaces keep every name whole.
function adPlatformList(platforms: readonly LeadsAdCheckRegistryKey[]): string {
  return platforms
    .map((platform) => adPlatformLabel(platform).replace(/ /g, "\u00a0"))
    .join(", ")
}

// The surface each hit was read from, so a three-platform tooltip is scanned by
// shape before it is read.
export const AD_PLATFORM_ICONS: Record<
  LeadsAdCheckRegistryKey,
  ComponentType<{ className?: string }>
> = {
  meta_ad_library: FaFacebook,
  google_ads: FaGoogle,
  tiktok_ad_library: FaTiktok,
  pages_jaunes: BookText,
}

// Card order of the ad-check registries, matching the backend lookup order.
const AD_REGISTRY_ORDER: readonly LeadsAdCheckRegistryKey[] = [
  "meta_ad_library",
  "google_ads",
  "tiktok_ad_library",
  "pages_jaunes",
]

type AdRegistries = NonNullable<LeadsAdCheck["registries"]>

// Leads checked before the per-registry answer existed carry only aggregate
// fields; the same answers are rebuilt from them. A reached platform with no
// hit found nothing, the dated stopped ad is past, and a hit runs today. Later
// pairs win, so a running ad outranks a past one on the same platform.
function legacyAdRegistries(adCheck: LeadsAdCheck | undefined): AdRegistries {
  const lastAd = adCheck?.last_ad
  const pairs: [LeadsAdCheckRegistryKey, LeadsAdCheckRegistry][] = [
    ...(adCheck?.checked_platforms ?? []).map(
      (platform): [LeadsAdCheckRegistryKey, LeadsAdCheckRegistry] => [
        platform,
        { status: "none" },
      ],
    ),
    ...(lastAd
      ? [
          [
            lastAd.platform,
            {
              status: "past",
              last_ad_at: lastAd.last_ad_at,
              proof_url: lastAd.proof_url,
            },
          ] satisfies [LeadsAdCheckRegistryKey, LeadsAdCheckRegistry],
        ]
      : []),
    ...(adCheck?.platforms ?? []).map(
      (hit): [LeadsAdCheckRegistryKey, LeadsAdCheckRegistry] => [
        hit.platform,
        { status: "active", proof_url: hit.proof_url },
      ],
    ),
  ]
  return Object.fromEntries(pairs)
}

// Ads seen at discovery fold into the same answers: an observed ad marks its
// platform as running where the ad check found nothing or could not look, and
// never overrides a running or past answer the ad check gave.
function withAdSignals(
  registries: AdRegistries,
  signals: readonly LeadsAdSignal[],
): AdRegistries {
  const pairs = signals
    .filter((signal) => {
      const status = registries[signal.platform]?.status
      return status === undefined || status === "none" || status === "unchecked"
    })
    .map((signal): [LeadsAdCheckRegistryKey, LeadsAdCheckRegistry] => [
      signal.platform,
      {
        status: "active",
        last_ad_at: signal.last_seen_at,
        proof_url: signal.proof_url,
      },
    ])
  return { ...registries, ...Object.fromEntries(pairs) }
}

// The icon alone carries the answer, three clear steps of ink: full for a
// running campaign, muted for a past one, near-blank for none (fainter still
// when the registry could not be checked).
const AD_REGISTRY_ICON_CLASS: Record<LeadsAdCheckRegistry["status"], string> = {
  active: "text-foreground",
  past: "text-muted-foreground",
  none: "text-muted-foreground/30",
  unchecked: "text-muted-foreground/15",
}

// A network the socials check did not find: faint, not gone.
const SOCIAL_MISSING_ICON_CLASS = "text-muted-foreground/50"

// What a registry answered, with the day of its last ad when known. The
// reason a registry went unchecked is staff-only (the API strips it for
// everyone else): a customer just reads "not checked".
function adRegistryStatus(
  entry: LeadsAdCheckRegistry,
  tr: (key: keyof typeof LEADS_PANE_T) => string,
  dateLocale: string,
): string {
  const date = entry.last_ad_at
    ? new Date(entry.last_ad_at).toLocaleDateString(dateLocale, {
        dateStyle: "medium",
      })
    : ""
  if (entry.status === "active")
    return date
      ? tr("adRegistryActiveSeen").replace("{date}", date)
      : tr("adRegistryActive")
  if (entry.status === "past")
    return date
      ? tr("adRegistryPast").replace("{date}", date)
      : tr("adRegistryPastUndated")
  if (entry.status === "none") return tr("adRegistryNone")
  if (entry.reason === "no_city") return tr("adRegistryNoCity")
  if (entry.reason === "unreachable") return tr("adRegistryUnreachable")
  return tr("adRegistryUnchecked")
}

// A running ad the check saw without a date was still live on the day of the
// check: that day stands in as its last sighting.
function withCheckDate(
  registries: AdRegistries,
  checkedAt: string | undefined,
): AdRegistries {
  if (!checkedAt) return registries
  return Object.fromEntries(
    Object.entries(registries).map(([platform, entry]) => [
      platform,
      entry?.status === "active" && !entry.last_ad_at
        ? { ...entry, last_ad_at: checkedAt }
        : entry,
    ]),
  )
}

// One row of an online presence list: icon, name, detail, and the outbound
// link when there is one.
function SignalRow({
  Icon,
  iconClassName,
  title,
  detail,
  badge,
  href,
}: {
  Icon: ComponentType<{ className?: string }>
  iconClassName: string
  title: string
  detail: string
  badge?: ReactNode
  href: string | undefined
}) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className={`group/signal focus-visible:ring-ring -ml-2 grid grid-cols-[auto_minmax(0,1fr)_auto] items-start gap-x-2 rounded-sm px-2 py-1.5 outline-none focus-visible:ring-2 ${href ? "hover:bg-accent" : ""}`}
    >
      <Icon className={`mt-0.5 size-3 shrink-0 ${iconClassName}`} />
      <span className="min-w-0">
        <span className="flex items-center gap-1.5 font-medium">
          {title}
          {badge}
        </span>
        {/* One line; the full link (or text) on hover. */}
        <span
          className="text-muted-foreground block truncate leading-snug"
          title={href ?? detail}
        >
          {detail}
        </span>
      </span>
      {href ? (
        // Centred on the two lines, quiet until the row is pointed at.
        <ArrowUpRight className={SIGNAL_LINK_ICON_CLASS} />
      ) : (
        <span />
      )}
    </a>
  )
}

function adRegistryEntries(registries: AdRegistries) {
  return AD_REGISTRY_ORDER.flatMap((platform) => {
    const entry = registries[platform]
    return entry ? [{ platform, entry }] : []
  })
}

/** One icon per ad registry: the megaphone leads, each registry's answer as its icon's ink. */
function AdRegistriesChip({ registries }: { registries: AdRegistries }) {
  const rows = adRegistryEntries(registries)
  const LeadIcon = rows.some((row) => row.entry.status === "active")
    ? Megaphone
    : MegaphoneOff
  return (
    // Icons only, no text line: py-1 matches the text badges' height.
    <Badge variant="outline" className="gap-1.5 py-1">
      <LeadIcon className="size-3" />
      {/* An unchecked registry has no answer to show: no logo for it. */}
      {rows.flatMap(({ platform, entry }) => {
        if (entry.status === "unchecked") return []
        const Icon = AD_PLATFORM_ICONS[platform]
        return (
          <Icon
            key={platform}
            className={`size-3 ${AD_REGISTRY_ICON_CLASS[entry.status]}`}
          />
        )
      })}
    </Badge>
  )
}

/**
 * Every registry with its answer, in the chip's order: running ads, past ads
 * (with when), none found, or not checked. Linked to the proof when there is one.
 */
function AdRegistriesDetails({ registries }: { registries: AdRegistries }) {
  const tr = useTr(LEADS_PANE_T)
  const dateLocale = useDateLocale()
  const isStaff = Boolean(useCurrentUser().data?.is_staff)
  return (
    <div>
      {adRegistryEntries(registries).map(({ platform, entry }) => {
        const withReason = isStaff && entry.status === "unchecked"
        return (
          <SignalRow
            key={platform}
            Icon={AD_PLATFORM_ICONS[platform]}
            iconClassName={AD_REGISTRY_ICON_CLASS[entry.status]}
            title={adPlatformLabel(platform)}
            detail={
              entry.status === "unchecked" && !isStaff
                ? tr("adRegistryUnchecked")
                : adRegistryStatus(entry, tr, dateLocale)
            }
            badge={
              withReason ? (
                <Badge
                  variant="secondary"
                  className="text-2xs px-1.5 py-0 font-normal"
                  title={tr("staffOnlyHint")}
                >
                  {tr("staffBadge")}
                </Badge>
              ) : null
            }
            href={
              entry.proof_url && isHttpUrl(entry.proof_url)
                ? entry.proof_url
                : undefined
            }
          />
        )
      })}
    </div>
  )
}

export const SOCIAL_ICONS: Record<string, IconType> = {
  linkedin: FaLinkedin,
  facebook: FaFacebook,
  instagram: FaInstagram,
  twitter: FaXTwitter,
  youtube: FaYoutube,
  tiktok: FaTiktok,
}

// Card order of the social networks: every supported one, found or not.
const SOCIAL_ORDER: readonly LeadsSocialPlatform[] = [
  "linkedin",
  "facebook",
  "instagram",
  "youtube",
  "tiktok",
  "twitter",
]

// The profile as a reader would type it: host and path, no scheme, no "www.".
function socialHandle(url: string): string {
  try {
    const parsed = new URL(url)
    const path = parsed.pathname.replace(/\/+$/, "")
    return `${parsed.hostname.replace(/^www\./, "")}${path}`
  } catch {
    return url
  }
}

/** One icon per supported network, muted when not found. */
function SocialsChip({ socials }: { socials: LeadsSocials }) {
  return (
    // Icons only, no text line: py-1 matches the text badges' height.
    <Badge variant="outline" className="gap-1.5 py-1">
      <Link className="size-3" />
      {SOCIAL_ORDER.map((platform) => {
        const Icon = SOCIAL_ICONS[platform]
        return (
          <Icon
            key={platform}
            className={`size-3 ${socials[platform] ? "" : SOCIAL_MISSING_ICON_CLASS}`}
          />
        )
      })}
    </Badge>
  )
}

/** Only the profiles found, each linked; the chip mutes the others. */
function SocialsDetails({ socials }: { socials: LeadsSocials }) {
  const tr = useTr(LEADS_PANE_T)
  const found = SOCIAL_ORDER.flatMap((platform) => {
    const url = socials[platform]
    return url ? [{ platform, url }] : []
  })
  if (found.length === 0) {
    return (
      <p className="text-muted-foreground py-1.5">
        {tr("socialsNoneFoundLine")}
      </p>
    )
  }
  return (
    <div>
      {found.map(({ platform, url }) => (
        <SignalRow
          key={platform}
          Icon={SOCIAL_ICONS[platform]}
          iconClassName="text-foreground"
          title={socialName(platform)}
          detail={socialHandle(url)}
          href={isHttpUrl(url) ? url : undefined}
        />
      ))}
    </div>
  )
}

// One category of the online presence section: a lighter chevron row with
// the category's chip on the right, its detail rows once open.
function PresenceSubsection({
  value,
  title,
  chip,
  children,
}: {
  value: string
  title: ReactNode
  chip: ReactNode
  children: ReactNode
}) {
  return (
    <AccordionItem value={value} className="border-0">
      <AccordionPrimitive.Header className="flex">
        <AccordionPrimitive.Trigger className="group/presence hover:bg-accent/40 focus-visible:ring-ring -mx-1.5 flex min-h-8 min-w-0 flex-1 items-center gap-1.5 rounded-md px-1.5 py-1 text-left outline-none focus-visible:ring-2 focus-visible:ring-inset">
          <ChevronRight className="text-muted-foreground size-3 shrink-0 transition-transform duration-200 group-data-[state=open]/presence:rotate-90 motion-reduce:transition-none" />
          <span className="text-foreground shrink-0 font-medium">{title}</span>
          <span className="text-2xs ml-auto flex shrink-0 items-center pl-2">
            {chip}
          </span>
        </AccordionPrimitive.Trigger>
      </AccordionPrimitive.Header>
      <AccordionPrimitive.Content className="motion-safe:data-[state=closed]:animate-accordion-up motion-safe:data-[state=open]:animate-accordion-down overflow-hidden">
        {/* Under the title text, past the chevron and its gap. */}
        <div className="pb-1.5 pl-4.5">{children}</div>
      </AccordionPrimitive.Content>
    </AccordionItem>
  )
}

/**
 * How the business shows up online: ratings, ads, social profiles. Closed,
 * the row carries every category's chip; open, each category carries its own
 * and unfolds to its rows. Nothing when no category has data.
 */
function OnlinePresenceSection({
  lead,
  adRegistries,
}: {
  lead: Lead
  adRegistries: AdRegistries
}) {
  const tr = useTr(LEADS_PANE_T)
  const ratings = summarizeRatings(lead)
  const hasAds = Object.keys(adRegistries).length > 0
  const socials = lead.socials_checked ? (lead.socials ?? {}) : null
  if (!ratings && !hasAds && !socials) return null
  return (
    <LeadSection
      value="presence"
      title={tr("presenceToggle")}
      wrapAside
      aside={
        <span className="text-2xs flex flex-wrap items-center justify-end gap-1.5 group-data-[state=open]/section:hidden">
          {ratings && <LeadRatingChip summary={ratings} />}
          {hasAds && <AdRegistriesChip registries={adRegistries} />}
          {socials && <SocialsChip socials={socials} />}
        </span>
      }
    >
      <Accordion type="multiple">
        {ratings && (
          <PresenceSubsection
            value="ratings"
            title={tr("presenceRatings")}
            chip={<LeadRatingChip summary={ratings} />}
          >
            <LeadRatingDetails summary={ratings} />
          </PresenceSubsection>
        )}
        {hasAds && (
          <PresenceSubsection
            value="ads"
            title={tr("presenceAds")}
            chip={<AdRegistriesChip registries={adRegistries} />}
          >
            <AdRegistriesDetails registries={adRegistries} />
          </PresenceSubsection>
        )}
        {socials && (
          <PresenceSubsection
            value="socials"
            title={tr("presenceSocials")}
            chip={<SocialsChip socials={socials} />}
          >
            <SocialsDetails socials={socials} />
          </PresenceSubsection>
        )}
      </Accordion>
    </LeadSection>
  )
}

// Brand names as the brands write them; an unknown platform gets a capital.
const SOCIAL_NAMES: Record<string, string> = {
  linkedin: "LinkedIn",
  facebook: "Facebook",
  instagram: "Instagram",
  twitter: "X",
  youtube: "YouTube",
  tiktok: "TikTok",
}

function socialName(platform: string): string {
  return (
    SOCIAL_NAMES[platform] ??
    platform.charAt(0).toUpperCase() + platform.slice(1)
  )
}

// A person's own profiles other than LinkedIn (the contact row links that one
// from the person's own field).
function personSocialEntries(socials?: LeadsSocials): [string, string][] {
  return Object.entries(socials ?? {}).filter(
    (entry): entry is [string, string] =>
      entry[0] !== "linkedin" && Boolean(entry[1]),
  )
}

// One contact's ways in on a single line: the email first (cut with an
// ellipsis, whole on hover), then each profile as an icon link named for
// screen readers. Without an email, the icons start the line.
function ContactChannels({ person }: { person: LeadPerson }) {
  const tr = useTr(LEADS_PANE_T)
  const email = person.emails[0]
  const linkedin = isHttpUrl(person.linkedin_url)
    ? person.linkedin_url
    : undefined
  const socials = personSocialEntries(person.socials)
  if (!email && !linkedin && socials.length === 0) return null
  return (
    <div className="text-muted-foreground flex min-w-0 items-center gap-2">
      {email && (
        <span className="flex min-w-0 items-center gap-1" title={email}>
          <Mail className="size-3 shrink-0" />
          <span className="truncate">{email}</span>
        </span>
      )}
      {linkedin && (
        <a
          href={linkedin}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={tr("linkedinProfile")}
          title={tr("linkedinProfile")}
          className="hover:text-foreground shrink-0"
        >
          <Linkedin className="size-3" />
        </a>
      )}
      {socials.map(([platform, url]) => {
        const Icon = SOCIAL_ICONS[platform] ?? Share2
        return (
          <a
            key={platform}
            href={isHttpUrl(url) ? url : undefined}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={socialName(platform)}
            title={`${socialName(platform)}: ${url}`}
            className="hover:text-foreground shrink-0"
          >
            <Icon className="size-3" />
          </a>
        )
      })}
    </div>
  )
}

// Best contacts first: decision makers (gold star for hunter, gray star for
// websearch), then whoever is reachable on the most channels, email first.
// Phones are not a dimension here: they are the lead's dialling list, not a
// channel of the person they happened to be scraped next to.
function personRank(person: LeadPerson): number {
  if (person.from_decision_makers) {
    return person.decision_maker_source === "websearch" ? 1 : 0
  }
  const email = person.emails.length > 0 || person.has_email === true
  const linkedin = Boolean(person.linkedin_url)
  if (email && linkedin) return 2
  if (email) return 3
  if (linkedin) return 4
  return 5
}

// Whether this entry is a contact of its own, or only a line of the lead's
// phone roster. A number reaches the company rather than a specific person, so
// an entry carrying nothing else is shown with the roster, not as a contact.
function isContact(person: LeadPerson): boolean {
  return (
    person.emails.length > 0 ||
    person.has_email === true ||
    Boolean(person.linkedin_url)
  )
}

// Edit / delete of one contact entry, shared by the contact rows and the phone
// lines (a number edits the entry it was scraped on, since a phone-only entry
// has no contact row of its own). On a mouse-driven screen the controls stay
// out of the way until the row is hovered or focused; touch screens always
// show them, and keyboard focus reveals them.
function RowActions({
  owner,
  onEdit,
  onDelete,
}: {
  owner: LeadPerson
  onEdit: (person: LeadPerson) => void
  onDelete: (person: LeadPerson) => void
}) {
  const tr = useTr(LEADS_PANE_T)
  return (
    <span className="text-muted-foreground flex shrink-0 items-center transition-opacity pointer-fine:opacity-0 pointer-fine:group-focus-within:opacity-100 pointer-fine:group-hover:opacity-100">
      <button
        type="button"
        title={tr("contactEdit")}
        aria-label={tr("contactEdit")}
        onClick={() => onEdit(owner)}
        className="hover:text-foreground hover:bg-accent focus-visible:ring-ring rounded-sm p-1 outline-none focus-visible:ring-2"
      >
        <Pencil className="size-3" />
      </button>
      <button
        type="button"
        title={tr("contactDelete")}
        aria-label={tr("contactDelete")}
        onClick={() => onDelete(owner)}
        className="hover:text-destructive hover:bg-accent focus-visible:ring-ring rounded-sm p-1 outline-none focus-visible:ring-2"
      >
        <Trash2 className="size-3" />
      </button>
    </span>
  )
}

// A column label with its count, so "Contacts 2 / Phone numbers 3" reads at a
// glance without boxing either list. The add action sits right after it: it
// is the column's own verb, read in the same glance as its title.
function ColumnHeading({
  id,
  label,
  count,
  addLabel,
  onAdd,
}: {
  id: string
  label: string
  count: number
  addLabel: string
  onAdd?: () => void
}) {
  const tr = useTr(LEADS_PANE_T)
  return (
    <div className="mb-1 flex min-h-6 items-center gap-1.5">
      <p
        id={id}
        className="text-foreground text-2sm flex items-baseline gap-1.5 font-medium"
      >
        {label}
        <span className="text-muted-foreground text-xs font-normal tabular-nums">
          {count}
        </span>
      </p>
      {onAdd && (
        <button
          type="button"
          onClick={onAdd}
          aria-label={addLabel}
          title={addLabel}
          className="text-muted-foreground hover:text-foreground hover:bg-accent focus-visible:ring-ring inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 outline-none focus-visible:ring-2"
        >
          <Plus className="size-3" />
          {tr("contactAddShort")}
        </button>
      )}
    </div>
  )
}

// The agent's contact order as a quiet "#3" chip; the tooltip spells it out as
// an ordinal ("3e position"). The order is
// computed over every entry the campaign's sequence channels can reach (see
// person_candidates), phone-only entries included; an entry none of them
// reaches has no rank. The chip shows on both the contact row and the phone
// line of a ranked entry, so the order reads whole across both columns.
function RankChip({ rank, total }: { rank: number; total: number }) {
  const tr = useTr(LEADS_PANE_T)
  const locale = useAutoleadsLocale()
  const tip = fill(tr("outreachRankTip"), {
    rank: ordinal(rank, locale),
    total: String(total),
  })
  // Focusable so the tooltip also opens from the keyboard.
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span
          tabIndex={0}
          aria-label={tip}
          className="bg-muted text-muted-foreground text-2xs focus-visible:ring-ring shrink-0 cursor-default rounded-sm px-1 tabular-nums outline-none focus-visible:ring-2"
        >
          #{rank}
        </span>
      </TooltipTrigger>
      <TooltipContent className="max-w-64">{tip}</TooltipContent>
    </Tooltip>
  )
}

// A contact the agent will not reach: none of the channels its sequence uses
// (see person_candidates) has an address on this entry. The lead does not
// carry the sequence's channels, so the tooltip states the rule, not the list.
function UnrankedChip() {
  const tr = useTr(LEADS_PANE_T)
  const tip = tr("outreachUnrankedTip")
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span
          tabIndex={0}
          aria-label={tip}
          className="border-border text-muted-foreground text-2xs focus-visible:ring-ring shrink-0 cursor-default rounded-sm border border-dashed px-1 outline-none focus-visible:ring-2"
        >
          {tr("outreachUnranked")}
        </span>
      </TooltipTrigger>
      <TooltipContent className="max-w-64">{tip}</TooltipContent>
    </Tooltip>
  )
}

// Where a contact came from when a person touched it: written by hand, or a
// scraped entry edited by hand (enrichment no longer overwrites it).
function contactOrigin(person: LeadPerson): {
  label: keyof typeof LEADS_PANE_T & string
  hint: keyof typeof LEADS_PANE_T & string
} | null {
  if (person.manual) {
    return { label: "contactManualBadge", hint: "contactManualHint" }
  }
  if (person.edited_at) {
    return { label: "contactEditedBadge", hint: "contactEditedHint" }
  }
  return null
}

// One contact as a flat row: name, role and its order on the first line, the
// ways in below, the row actions on the right edge.
function ContactRow({
  person,
  rankTotal,
  question,
  editable,
  onEdit,
  onDelete,
}: {
  person: LeadPerson
  rankTotal: number
  question: string | null
  editable: boolean
  onEdit: (person: LeadPerson) => void
  onDelete: (person: LeadPerson) => void
}) {
  const tr = useTr(LEADS_PANE_T)
  const star =
    DECISION_MAKER_STAR[person.decision_maker_source ?? ""] ??
    DECISION_MAKER_STAR[""]
  const origin = contactOrigin(person)
  return (
    <li className="group hover:bg-accent/50 -mx-1.5 flex items-start gap-2 rounded-md px-1.5 py-2">
      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
        <div className="flex min-h-5 min-w-0 items-center gap-1.5">
          {person.from_decision_makers && (
            <span className="flex shrink-0 items-center" title={tr(star.tip)}>
              <Star className={cn("size-3", star.className)} />
            </span>
          )}
          {/* One text run, so a long role is cut before the name is. The
              name leads the row a step up (13/500); everything under it
              stays at 12, muted. */}
          <span className="min-w-0 truncate">
            <span className="text-foreground text-2sm font-medium">
              {person.name || tr("unknown")}
            </span>
            {person.role && person.role !== person.name && (
              <span className="text-muted-foreground ml-1.5">
                {person.role}
              </span>
            )}
          </span>
          {person.outreach_rank != null ? (
            <RankChip rank={person.outreach_rank} total={rankTotal} />
          ) : (
            // The agent ranked this lead but left this contact out: say so
            // where its order would be. Only on the working card, where the
            // ranking is the rep's to act on.
            editable && rankTotal > 0 && <UnrankedChip />
          )}
          {origin && (
            <Badge
              variant="secondary"
              className="text-2xs shrink-0 px-1.5 py-0 font-normal"
              title={tr(origin.hint)}
            >
              {tr(origin.label)}
            </Badge>
          )}
        </div>
        <ContactChannels person={person} />
        {person.custom_enrichment && person.custom_enrichment.length > 0 && (
          <CustomEnrichmentChips
            entries={person.custom_enrichment}
            question={question}
            maxVisible={ENRICHMENT_MAX_VISIBLE}
          />
        )}
      </div>
      {editable && person.index != null && (
        <div className="flex min-h-5 shrink-0 items-center">
          <RowActions owner={person} onEdit={onEdit} onDelete={onDelete} />
        </div>
      )}
    </li>
  )
}

// The lead's dialling list: a rep calls every number of a company in one pass,
// so the numbers are the lead's and not a contact's. The number leads each line
// (it is what gets dialled); whoever it was scraped next to follows, muted,
// and the line edits that entry so a wrong number stays fixable. No order chip:
// AutoLeads never dials, a call is a rep's task, so only the contacts it works
// on its own are numbered.
function PhoneRoster({
  lines,
  contactByIndex,
  editable,
  onEdit,
  onDelete,
}: {
  lines: LeadPhoneContact[]
  contactByIndex: Map<number | undefined, LeadPerson>
  editable: boolean
  onEdit: (person: LeadPerson) => void
  onDelete: (person: LeadPerson) => void
}) {
  const tr = useTr(LEADS_PANE_T)
  const [showAll, setShowAll] = useState(false)
  const visible = showAll ? lines : lines.slice(0, PHONE_ROSTER_MAX_VISIBLE)
  const hidden = lines.length - visible.length
  return (
    <>
      <ul className="divide-border flex flex-col divide-y divide-dotted">
        {visible.map((line) => {
          const owner = contactByIndex.get(line.index)
          const origin = owner ? contactOrigin(owner) : null
          const label = [
            line.name,
            line.role && line.role !== line.name ? line.role : "",
          ]
            .filter(Boolean)
            .join(" · ")
          return (
            // Laid out like a contact row: whoever the number was scraped
            // next to leads ("Office"), the number and its provenance under it,
            // the actions on the first line's right edge. Without a label the
            // number leads, so a row is never headless.
            <li
              key={line.phone}
              className="group hover:bg-accent/50 -mx-1.5 flex items-start gap-2 rounded-md px-1.5 py-2"
            >
              <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                <div className="flex min-h-5 min-w-0 items-center">
                  {label ? (
                    // One text run like the contact row's name and role, so a
                    // long role is cut before the name is.
                    <span className="min-w-0 truncate" title={label}>
                      <span className="text-foreground text-2sm font-medium">
                        {line.name || line.role}
                      </span>
                      {line.name && line.role && line.role !== line.name && (
                        <span className="text-muted-foreground ml-1.5">
                          {line.role}
                        </span>
                      )}
                    </span>
                  ) : (
                    <PhoneLink
                      phone={line.phone}
                      className="text-foreground text-2sm shrink-0 font-medium"
                    />
                  )}
                </div>
                {(label || origin) && (
                  <div className="text-muted-foreground flex min-w-0 items-center gap-1.5">
                    {label && (
                      <PhoneLink phone={line.phone} className="shrink-0" />
                    )}
                    {origin && (
                      <Badge
                        variant="secondary"
                        className="text-2xs shrink-0 px-1.5 py-0 font-normal"
                        title={tr(origin.hint)}
                      >
                        {tr(origin.label)}
                      </Badge>
                    )}
                  </div>
                )}
              </div>
              {editable && owner && (
                <div className="flex min-h-5 shrink-0 items-center">
                  <RowActions
                    owner={owner}
                    onEdit={onEdit}
                    onDelete={onDelete}
                  />
                </div>
              )}
            </li>
          )
        })}
      </ul>
      {lines.length > PHONE_ROSTER_MAX_VISIBLE && (
        <ShowMoreToggle
          expanded={showAll}
          onToggle={() => setShowAll((prev) => !prev)}
          moreLabel={fill(tr("phonesShowMore"), { n: hidden })}
        />
      )}
    </>
  )
}

// The "+N" toggle under a capped list: it expands the list in place, so the
// card grows instead of nesting a scroll box in it.
function ShowMoreToggle({
  expanded,
  onToggle,
  moreLabel,
}: {
  expanded: boolean
  onToggle: () => void
  moreLabel: string
}) {
  const tr = useTr(LEADS_PANE_T)
  return (
    <button
      type="button"
      onClick={onToggle}
      className="text-muted-foreground hover:text-foreground hover:bg-accent mt-1 -ml-1.5 inline-flex items-center gap-1 rounded-md px-1.5 py-1 font-medium"
    >
      {expanded ? (
        <>
          <ChevronUp className="size-3" />
          {tr("peopleShowLess")}
        </>
      ) : (
        <>
          <ChevronDown className="size-3" />
          {moreLabel}
        </>
      )}
    </button>
  )
}

// Which form the contact dialog opens on: a new contact, a new number, or an
// existing entry.
type ContactEditing = LeadPerson | "new-contact" | "new-phone" | null

// The contacts of one lead on the left, its phone numbers on the right. The
// two lists are independent: contacts are ranked people with their own ways
// in, the numbers are the lead's dialling list. They stack on a narrow card.
function LeadPeopleList({
  people,
  phoneRoster,
  question,
  leadId,
  editable = false,
}: {
  people: LeadPerson[]
  // The lead's numbers, best first.
  phoneRoster: LeadPhoneContact[]
  question: string | null
  leadId: number
  // Contact editing is opt-in: the same card renders on the public sample and
  // onboarding pages, where there is nothing to write to.
  editable?: boolean
}) {
  const tr = useTr(LEADS_PANE_T)
  const peopleHeadingId = useId()
  const phonesHeadingId = useId()
  const [showAll, setShowAll] = useState(false)
  const [editing, setEditing] = useState<ContactEditing>(null)
  const [deleting, setDeleting] = useState<LeadPerson | null>(null)
  const deleteContact = useDeleteLeadContact({
    successMessage: tr("contactDeleted"),
    onSuccess: () => setDeleting(null),
  })
  // The agent's ladder is the truth when it ran: it is the order the CRM enrols
  // these people in, and it weighs contact quality (a CEO's own email beats a
  // switchboard number) instead of channel count. personRank only orders the
  // leads it has not reached yet.
  // Only the people who are contacts of their own get a row: a phone-only
  // entry lives in the phone column.
  const ranked = people
    .filter(isContact)
    .sort(
      (a, b) =>
        (a.outreach_rank ?? Number.MAX_SAFE_INTEGER) -
          (b.outreach_rank ?? Number.MAX_SAFE_INTEGER) ||
        personRank(a) - personRank(b),
    )
  const visible = showAll ? ranked : ranked.slice(0, PEOPLE_MAX_VISIBLE)
  const hidden = ranked.length - visible.length
  const contactByIndex = new Map(
    people
      .filter((person) => person.index != null)
      .map((person) => [person.index, person]),
  )
  // The agent numbers only the contacts it works on its own (no call-only
  // entry), so this is the "out of N" of the contacts column.
  const rankTotal = people.filter((p) => p.outreach_rank != null).length
  const hasContacts = ranked.length > 0
  const hasPhones = phoneRoster.length > 0
  // An editable card keeps both columns, empty ones included, so each keeps
  // its add action in a predictable place.
  const showContacts = editable || hasContacts
  const showPhones = editable || hasPhones
  const twoColumns = showContacts && showPhones
  if (!showContacts && !showPhones) return null
  return (
    <div className="@container text-xs">
      <div
        className={cn("grid gap-x-6 gap-y-5", twoColumns && "@lg:grid-cols-5")}
      >
        {showContacts && (
          <section
            aria-labelledby={peopleHeadingId}
            className={cn("min-w-0", twoColumns && "@lg:col-span-3")}
          >
            <ColumnHeading
              id={peopleHeadingId}
              label={tr("peopleHeading")}
              count={ranked.length}
              addLabel={tr("contactAdd")}
              onAdd={editable ? () => setEditing("new-contact") : undefined}
            />
            {hasContacts ? (
              // A dotted rule between contacts (never above the first or
              // below the last), centred in the rows' own padding.
              <ul className="divide-border flex flex-col divide-y divide-dotted">
                {visible.map((person, idx) => (
                  <ContactRow
                    key={`${person.name || "anon"}-${idx}`}
                    person={person}
                    rankTotal={rankTotal}
                    question={question}
                    editable={editable}
                    onEdit={setEditing}
                    onDelete={setDeleting}
                  />
                ))}
              </ul>
            ) : (
              <p className="text-muted-foreground/70">{tr("contactsEmpty")}</p>
            )}
            {ranked.length > PEOPLE_MAX_VISIBLE && (
              <ShowMoreToggle
                expanded={showAll}
                onToggle={() => setShowAll((prev) => !prev)}
                moreLabel={fill(tr("peopleShowMore"), { n: hidden })}
              />
            )}
          </section>
        )}
        {showPhones && (
          <section
            aria-labelledby={phonesHeadingId}
            className={cn(
              "min-w-0",
              twoColumns &&
                "@lg:border-border/70 @lg:col-span-2 @lg:border-l @lg:pl-6",
            )}
          >
            <ColumnHeading
              id={phonesHeadingId}
              label={tr("phoneRoster")}
              count={phoneRoster.length}
              addLabel={tr("contactAddPhone")}
              onAdd={editable ? () => setEditing("new-phone") : undefined}
            />
            {hasPhones ? (
              <PhoneRoster
                lines={phoneRoster}
                contactByIndex={contactByIndex}
                editable={editable}
                onEdit={setEditing}
                onDelete={setDeleting}
              />
            ) : (
              <p className="text-muted-foreground/70">{tr("phonesEmpty")}</p>
            )}
          </section>
        )}
      </div>
      {editing && (
        <LeadContactDialog
          leadId={leadId}
          person={typeof editing === "string" ? undefined : editing}
          intent={editing === "new-phone" ? "phone" : "contact"}
          onClose={() => setEditing(null)}
        />
      )}
      <AlertDialog
        open={deleting !== null}
        onOpenChange={(open) => !open && setDeleting(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{tr("contactDelete")}</AlertDialogTitle>
            <AlertDialogDescription>
              {tr("contactDeleteConfirm")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{tr("contactCancel")}</AlertDialogCancel>
            <AlertDialogAction
              disabled={deleteContact.isPending}
              onClick={(event) => {
                // The row stays open until the request answers, so the button
                // can show its pending state instead of the dialog vanishing.
                event.preventDefault()
                if (deleting?.index == null) return
                deleteContact.mutate({
                  lead_id: leadId,
                  index: deleting.index,
                })
              }}
            >
              {deleteContact.isPending && (
                <Loader2 className="size-4 animate-spin" />
              )}
              {tr("contactDelete")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}

// How a named decision-maker is starred. Only Hunter carries a verified email,
// so it keeps the emphasis; a name from the web or from the company registry is
// muted, and the tooltip says where it came from. The empty key is the default
// for rows written before the source was recorded.
const DECISION_MAKER_STAR: Record<
  string,
  {
    tip: keyof typeof LEADS_PANE_T & string
    // Two or three words for the people panel, where every row carries its
    // origin and the full sentence would drown the name and the role.
    label: keyof typeof LEADS_PANE_T & string
    className: string
  }
> = {
  "": {
    tip: "hunterDecisionMaker",
    label: "decisionMakerOriginHunter",
    className: "text-primary-cta fill-primary-cta",
  },
  hunter: {
    tip: "hunterDecisionMaker",
    label: "decisionMakerOriginHunter",
    className: "text-primary-cta fill-primary-cta",
  },
  websearch: {
    tip: "websearchDecisionMaker",
    label: "decisionMakerOriginWebsearch",
    className: "text-muted-foreground fill-muted-foreground",
  },
  pappers: {
    tip: "pappersDecisionMaker",
    label: "decisionMakerOriginPappers",
    className: "text-muted-foreground fill-muted-foreground",
  },
  contact: {
    tip: "contactDecisionMaker",
    label: "decisionMakerOriginContact",
    className: "text-muted-foreground fill-muted-foreground",
  },
}

// Fiche Pappers: the company registry record behind a pappers-sourced lead.
// The backend ships stable section/row keys (see services/pappers_fiche.py) and
// pre-rendered values; only the labels and the dates are localized here.
const PAPPERS_SECTION_LABELS: Record<
  string,
  keyof typeof LEADS_PANE_T & string
> = {
  identity: "pappersSectionIdentity",
  activity: "pappersSectionActivity",
  head_office: "pappersSectionHeadOffice",
  finance: "pappersSectionFinance",
  people: "pappersSectionPeople",
}

const PAPPERS_ROW_LABELS: Record<string, keyof typeof LEADS_PANE_T & string> = {
  siren: "pappersRowSiren",
  company_number: "pappersRowCompanyNumber",
  legal_form: "pappersRowLegalForm",
  status: "pappersRowStatus",
  created_at: "pappersRowCreatedAt",
  ceased_at: "pappersRowCeasedAt",
  capital: "pappersRowCapital",
  naf: "pappersRowNaf",
  activity_domain: "pappersRowActivityDomain",
  activities: "pappersRowActivities",
  local_activities: "pappersRowLocalActivities",
  purpose: "pappersRowPurpose",
  collective_agreements: "pappersRowCollectiveAgreements",
  address: "pappersRowAddress",
  siret: "pappersRowSiret",
  // societe.com only: it publishes the VAT number and the declared purpose,
  // which the Pappers payload carries under other keys or not at all.
  vat_number: "pappersRowVatNumber",
  declared_activity: "pappersRowDeclaredActivity",
  establishments: "pappersRowEstablishments",
  workforce: "pappersRowWorkforce",
  revenue: "pappersRowRevenue",
  profit: "pappersRowProfit",
  finance_year: "pappersRowFinanceYear",
  // One row per officer. The older comma-joined key is kept because a lead
  // delivered before the change carries a snapshot fiche that still uses it.
  director: "pappersRowDirector",
  directors: "pappersRowDirectors",
  directors_count: "pappersRowDirectorsCount",
  beneficiaries_count: "pappersRowBeneficiariesCount",
  publications_count: "pappersRowPublicationsCount",
}

// Who works at this business, whatever we know about reaching them: the
// reachable ones the contact block drives outreach with, then the names the
// registry or the web search produced on their own. Each row carries where the
// name came from, and an icon per way in rather than the address itself, which
// the contact block already prints in full.
function DecisionMakersView({
  people,
  tr,
}: {
  people: DecisionMakerEntry[]
  tr: LeadsPaneTr
}) {
  return (
    <div>
      {/* Same row rhythm as the job offer rows. */}
      <ul>
        {people.map((person, index) => {
          const origin =
            DECISION_MAKER_STAR[person.source] ?? DECISION_MAKER_STAR[""]
          return (
            <li
              key={`${person.name}-${index}`}
              className="hover:bg-accent/50 -mx-1.5 flex items-center gap-2 rounded-md px-1.5 py-1 transition-colors"
            >
              <span className="text-foreground">{person.name}</span>
              {person.role && (
                <span className="text-muted-foreground">| {person.role}</span>
              )}
              <span className="text-muted-foreground" title={tr(origin.tip)}>
                ({tr(origin.label)})
              </span>
              {person.email && (
                <a
                  href={`mailto:${person.email}`}
                  className="text-muted-foreground hover:text-primary"
                  title={`${tr("decisionMakerEmail")} : ${person.email}`}
                >
                  <Mail className="size-3" />
                </a>
              )}
              {person.phone && (
                <a
                  href={`tel:${person.phone}`}
                  className="text-muted-foreground hover:text-primary"
                  title={`${tr("decisionMakerPhone")} : ${person.phone}`}
                >
                  <Phone className="size-3" />
                </a>
              )}
              {person.linkedin_url && (
                <a
                  href={person.linkedin_url}
                  target="_blank"
                  rel="noreferrer"
                  className="text-muted-foreground hover:text-primary"
                  title={tr("linkedinProfile")}
                >
                  <Linkedin className="size-3" />
                </a>
              )}
            </li>
          )
        })}
      </ul>
    </div>
  )
}

function PappersFicheView({
  fiche,
  tr,
}: {
  fiche: PappersFiche
  tr: LeadsPaneTr
}) {
  const dateLocale = useDateLocale()
  const formatValue = (row: PappersFicheRow) => {
    if (row.kind !== "date") return row.value
    const date = new Date(row.value)
    return Number.isNaN(date.getTime())
      ? row.value
      : date.toLocaleDateString(dateLocale)
  }
  // The registry states how many officers a company has, while the fiche only
  // names those the source hands over for free (Pappers lists the main ones in
  // its search payload, the rest sit behind its paid registry extract). Saying
  // "4" above two names reads as a bug, so a shorter list is called out.
  const partialDirectors = (section: PappersFicheSection) => {
    const total = Number(
      section.rows.find((row) => row.key === "directors_count")?.value,
    )
    const named = section.rows.filter((row) => row.key === "director").length
    return Number.isFinite(total) && total > named
  }
  return (
    <div className="space-y-3">
      {fiche.sections.map((section) => (
        <div key={section.key}>
          <p className="text-foreground font-medium">
            {PAPPERS_SECTION_LABELS[section.key]
              ? tr(PAPPERS_SECTION_LABELS[section.key])
              : section.key}
          </p>
          <dl className="mt-0.5 space-y-0.5">
            {section.rows.map((row, index) => (
              <div key={`${row.key}-${index}`} className="flex gap-2">
                <dt className="text-muted-foreground w-36 shrink-0">
                  {/* A section repeats a key (one row per officer): label the
                      first of a run only, so five officers read as one block. */}
                  {section.rows[index - 1]?.key === row.key
                    ? ""
                    : PAPPERS_ROW_LABELS[row.key]
                      ? tr(PAPPERS_ROW_LABELS[row.key])
                      : row.key}
                </dt>
                <dd className="min-w-0 flex-1 break-words">
                  {formatValue(row)}
                  {row.key === "directors_count" &&
                  partialDirectors(section) ? (
                    <span className="text-muted-foreground">
                      {" "}
                      {tr("companyDirectorsPartial")}
                    </span>
                  ) : null}
                </dd>
              </div>
            ))}
          </dl>
        </div>
      ))}
    </div>
  )
}

// Which company page a source sends the reader to.
type CompanyOpenLabel = "pappersOpenPage" | "societeOpenPage"

// The company page of the source being read: the link follows the open tab,
// so it always sends to the figures shown above it.
function CompanyFicheLink({
  fiche,
  openLabel,
  tr,
}: {
  fiche: PappersFiche
  openLabel: CompanyOpenLabel
  tr: LeadsPaneTr
}) {
  if (!isHttpUrl(fiche.url)) return null
  return (
    <div className="mt-2">
      <a
        href={fiche.url}
        target="_blank"
        rel="noreferrer"
        className="text-muted-foreground hover:text-primary inline-flex items-center gap-1 hover:underline"
      >
        {tr(openLabel)}
        <ExternalLink className="size-3" />
      </a>
    </div>
  )
}

// The company's registry record, one tab per source. Both lookups run under
// the same plugin and answer the same question, so they belong in one block:
// Pappers carries the figures it pays for (headcount, revenue), societe.com
// the ones its free pages publish (VAT number, collective agreement). A lead
// only one source identified shows that source alone, with no tab bar.
function CompanyFicheBlock({
  pappers,
  societe,
  tr,
}: {
  pappers: PappersFiche | null
  societe: PappersFiche | null
  tr: LeadsPaneTr
}) {
  const sources = [
    {
      key: "pappers",
      fiche: pappers,
      labelKey: "companyTabPappers",
      openLabel: "pappersOpenPage",
    },
    {
      key: "societe",
      fiche: societe,
      labelKey: "companyTabSociete",
      openLabel: "societeOpenPage",
    },
  ].filter((source) => source.fiche && source.fiche.sections.length > 0) as {
    key: string
    fiche: PappersFiche
    labelKey: "companyTabPappers" | "companyTabSociete"
    openLabel: CompanyOpenLabel
  }[]
  const [sourceKey, setSourceKey] = useState(sources[0]?.key ?? "")
  if (sources.length === 0) return null
  const current = sources.find((s) => s.key === sourceKey) ?? sources[0]

  return (
    <LeadSection
      value="company"
      title={tr("companyFicheToggle")}
      aside={<FileText className="size-3.5" />}
    >
      {sources.length > 1 ? (
        // The app's segmented tabs: a muted track with the open source
        // raised on it, arrow keys to move between sources.
        <Tabs
          value={current.key}
          onValueChange={setSourceKey}
          className="gap-3"
        >
          <TabsList size="compact">
            {sources.map((source) => (
              <TabsTrigger key={source.key} value={source.key}>
                {tr(source.labelKey)}
              </TabsTrigger>
            ))}
          </TabsList>
          {sources.map((source) => (
            <TabsContent key={source.key} value={source.key}>
              <PappersFicheView fiche={source.fiche} tr={tr} />
              <CompanyFicheLink
                fiche={source.fiche}
                openLabel={source.openLabel}
                tr={tr}
              />
            </TabsContent>
          ))}
        </Tabs>
      ) : (
        <>
          <PappersFicheView fiche={current.fiche} tr={tr} />
          <CompanyFicheLink
            fiche={current.fiche}
            openLabel={current.openLabel}
            tr={tr}
          />
        </>
      )}
    </LeadSection>
  )
}

// The drafted copy of the whole lead, one recipient and one email at a time:
// a place with five contacted people and a four-step sequence would otherwise
// stack twenty emails under the card. Recipients and steps are both compact
// tab rows, so the whole sequence is visible and one click away.
function MailingCopies({
  people,
}: {
  people: { person: LeadPerson; steps: SequenceStep[] }[]
}) {
  const [personIndex, setPersonIndex] = useState(0)
  const index = Math.min(personIndex, people.length - 1)
  const current = people[index]
  if (!current) return null

  const label = (person: LeadPerson) =>
    person.name || person.emails[0] || person.role
  // Keyed on the recipient: switching person starts again at step 1. The
  // other recipients' emails ride along as invisible sizers, so the preview
  // is as tall as the longest email of the whole lead, whoever is shown.
  const sequence = (
    <EmailSequence
      key={index}
      person={current.person}
      steps={current.steps}
      sizers={people
        .filter((_, personAt) => personAt !== index)
        .flatMap((entry) => entry.steps)}
    />
  )
  if (people.length < 2) return sequence
  return (
    <Tabs
      value={String(index)}
      onValueChange={(value) => setPersonIndex(Number(value))}
      className="gap-3"
    >
      <div className="max-w-full overflow-x-auto overflow-y-hidden">
        <TabsList size="compact">
          {people.map(({ person }, personAt) => (
            <TabsTrigger
              key={person.emails[0] ?? person.name}
              value={String(personAt)}
            >
              {label(person)}
            </TabsTrigger>
          ))}
        </TabsList>
      </div>
      <TabsContent value={String(index)}>{sequence}</TabsContent>
    </Tabs>
  )
}

// One email's body under its hairline. The recipient and subject sit above it
// in the sequence header, one line each, so only the bodies vary in height.
function EmailBody({ step }: { step: SequenceStep }) {
  if (!step.body) return null
  return (
    <div className="border-border/70 text-foreground mt-1 space-y-2 border-t pt-2 leading-relaxed">
      {step.body
        .split(/\n\s*\n/)
        .filter((paragraph) => paragraph.trim())
        .map((paragraph, paragraphAt) => (
          <p key={paragraphAt} className="whitespace-pre-wrap">
            <LinkedBody body={paragraph.trim()} />
          </p>
        ))}
    </div>
  )
}

// One recipient's sequence, one email at a time. The header carries the
// shown email's recipient and subject (one truncated line each) and the pager
// on its right, resting on the hairline; on a narrow card the pager takes its
// own line above it. Every body of the lead sits in the same grid cell: the
// shown one, the recipient's other steps and the `sizers` (the other
// recipients' emails), the hidden ones invisible, out of the accessibility
// tree and inert. The cell takes the tallest at the current width, so paging
// never resizes the box or moves the pager, with nothing measured in script.
// The steps carry no delay client side.
function EmailSequence({
  person,
  steps,
  sizers,
}: {
  person: LeadPerson
  steps: SequenceStep[]
  sizers: SequenceStep[]
}) {
  const tr = useTr(LEADS_PANE_T)
  const [stepIndex, setStepIndex] = useState(0)
  const index = Math.min(stepIndex, steps.length - 1)
  const step = steps[index]
  if (!step) return null
  const recipient = person.emails[0]
  // A subject row on every step as soon as one has a subject, so a follow-up
  // without one does not shift the header.
  const hasSubjects = steps.some((candidate) => candidate.subject)
  const subject = step.subject
    ? fill(tr("mailingSubject"), { subject: step.subject })
    : ""
  const go = (delta: number) =>
    setStepIndex(Math.max(0, Math.min(steps.length - 1, index + delta)))
  return (
    <div className="flex flex-col gap-1">
      <div className="flex flex-wrap items-end justify-end gap-x-3 gap-y-1">
        <div className="flex min-w-0 grow basis-56 flex-col gap-1">
          {recipient && (
            <p className="text-muted-foreground truncate">
              {fill(tr("mailingTo"), { email: recipient })}
            </p>
          )}
          {hasSubjects && (
            <p
              className="text-foreground truncate font-semibold"
              title={subject || undefined}
            >
              {subject || "\u00a0"}
            </p>
          )}
        </div>
        {steps.length > 1 && (
          // Arrow keys step through the sequence while focus is in the pager.
          <div
            role="group"
            aria-label={tr("mailingPager")}
            className="flex shrink-0 items-center gap-0.5"
            onKeyDown={(event) => {
              if (event.key === "ArrowLeft") go(-1)
              if (event.key === "ArrowRight") go(1)
            }}
          >
            <Button
              type="button"
              variant="ghost"
              size="xs"
              aria-label={tr("mailingPrevious")}
              title={tr("mailingPrevious")}
              disabled={index === 0}
              onClick={() => go(-1)}
            >
              <ChevronLeft />
            </Button>
            <span
              aria-live="polite"
              className="text-muted-foreground px-1 tabular-nums"
            >
              {fill(tr("mailingCounter"), {
                n: index + 1,
                total: steps.length,
              })}
            </span>
            <Button
              type="button"
              variant="ghost"
              size="xs"
              aria-label={tr("mailingNext")}
              title={tr("mailingNext")}
              disabled={index >= steps.length - 1}
              onClick={() => go(1)}
            >
              <ChevronRight />
            </Button>
          </div>
        )}
      </div>
      <div className="grid">
        {steps.map((candidate, stepAt) => {
          const hidden = stepAt !== index
          return (
            <div
              key={`step-${stepAt}`}
              className={cn("col-start-1 row-start-1", hidden && "invisible")}
              aria-hidden={hidden || undefined}
              inert={hidden}
            >
              <EmailBody step={candidate} />
            </div>
          )
        })}
        {sizers.map((sizer, sizerAt) => (
          <div
            key={`sizer-${sizerAt}`}
            className="invisible col-start-1 row-start-1"
            aria-hidden
            inert
          >
            <EmailBody step={sizer} />
          </div>
        ))}
      </div>
    </div>
  )
}

// scoringEnabled: the website-quality (SEO audit) plugin was picked on the
// campaign. Without it we never audited the site, so the no-website pitch block
// would be an unbacked claim.
export function LeadCard({
  lead,
  scoringEnabled = true,
  editable = false,
  downloadable = true,
  selection,
  reenriching = false,
  pilot = false,
  isPublic = false,
  onOpenAccount,
  onPilotActivate,
  extractionNames,
  extractionsActive = false,
}: {
  lead: Lead
  scoringEnabled?: boolean
  // Lets the reader add / edit / remove the lead's contacts. Off by default:
  // the sample and onboarding pages render the same card with no write access.
  editable?: boolean
  // Offers the card as a file. Off on the public sample and onboarding pages:
  // the export endpoint answers a signed-in reader only.
  downloadable?: boolean
  // Bulk selection (re-enrich). Absent = the card is not selectable.
  selection?: { selected: boolean; onToggle: () => void }
  // A paid re-enrich run is still queued or running on this lead.
  reenriching?: boolean
  // Shows the status menu and the next step: the overview and the board, where
  // the lead is worked. Off where the company is already shown around the card.
  pilot?: boolean
  // A public page (sample, onboarding): the custom-question section hides its
  // verdict, which would tell a reader what the client asked.
  isPublic?: boolean
  // Opens the company's CRM sheet from the next step.
  onOpenAccount?: (accountId: number) => void
  // What the next step does where the company is already open (the CRM
  // dialog jumps to its steps); used when there is no sheet to open.
  onPilotActivate?: () => void
  // Names of the campaign's extractions, for the AI verdicts.
  extractionNames?: Map<number, string>
  // The campaign attaches extractions: the card shows where the lead stands
  // and a left-out lead reads dimmer.
  extractionsActive?: boolean
}) {
  const tr = useTr(LEADS_PANE_T)
  const trR = useTr(REENRICH_T)
  const dateLocale = useDateLocale()
  // Ad-check proofs. Redacted to [] on buffered/anonymized leads, while has_ads
  // may still be true, so the "advertises" badge only renders with a proof link.
  // One ad pill for every lead with ad data: the per-registry answers, or the
  // same answers rebuilt from older leads' aggregate fields, plus discovery ads.
  const adRegistries = withAdSignals(
    withCheckDate(
      lead.ad_check?.registries ?? legacyAdRegistries(lead.ad_check),
      lead.ad_check?.checked_at,
    ),
    lead.ad_signals ?? [],
  )
  // Source signals sit on the address row, out of the core chips; ratings,
  // ads and socials live in the online presence section.
  const hasSignalBadges =
    Boolean(lead.watcher_event) || Boolean(lead.job_listing_url)
  const locale = useAutoleadsLocale()
  const displayName =
    lead.name ||
    lead.normalized_domain ||
    tr("leadNumber").replace("{id}", String(lead.id))
  const formatDate = (iso: string) =>
    new Date(iso).toLocaleDateString(dateLocale, { dateStyle: "medium" })
  // The delivery date lives in the batch (or import) tooltip, not on its own chip.
  const deliveredLine = lead.delivered_at ? (
    <p>{tr("deliveredOn").replace("{date}", formatDate(lead.delivered_at))}</p>
  ) : null
  // What the lead cost, in the batch tooltip; omitted when nothing was charged.
  const costLine = lead.credits_charged ? (
    <p>
      {fill(
        tr("batchCost"),
        {
          n: lead.credits_charged.toLocaleString(dateLocale),
          count: lead.credits_charged,
        },
        dateLocale,
      )}
    </p>
  ) : null
  // Copy is written per person: one sequence per contact we reach out to.
  const mailingPeople = lead.people
    .map((person) => ({ person, steps: sequenceSteps(person.mailing_copy) }))
    .filter((entry) => entry.steps.length > 0)
  if (lead.status === "buffered") return <BufferedLeadCard lead={lead} />
  return (
    // Bands split by full-bleed hairlines: identity, the next step, the
    // people, and the detail accordion as a flush footer. Each band pads 12px, so a line
    // sits in the middle of the 24px between two groups.
    <li
      className={cn(
        "border-border bg-card/40 flex flex-col overflow-hidden rounded-md border",
        // Left out by the extractions: still listed, but quieter than the kept.
        extractionsActive &&
          lead.extraction_segment === "excluded" &&
          "bg-muted/40 border-dashed",
      )}
    >
      <div className="p-3">
        <div>
          {/* From sm up the core chips keep their line and a long name truncates
          instead; on a phone they wrap under the name. */}
          <div className="flex flex-wrap items-start justify-between gap-2 sm:flex-nowrap">
            <div className="min-w-0 space-y-0.5">
              {/* On a phone the status and outdated badges drop under a long
              name instead of squeezing it; from sm up they stay on its line
              and the name truncates. */}
              <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm font-medium sm:flex-nowrap">
                <span className="flex min-w-0 items-center gap-2">
                  {selection ? (
                    <Checkbox
                      checked={selection.selected}
                      onCheckedChange={selection.onToggle}
                      aria-label={trR("selectLead")}
                    />
                  ) : null}
                  <SiteFavicon
                    website={lead.website}
                    domain={lead.normalized_domain}
                  />
                  <span className="min-w-0 truncate" title={displayName}>
                    {displayName}
                  </span>
                </span>
                {/* The status is worked where the lead is: the listing and the
                contact dialog, never the read-only surfaces. */}
                {pilot && lead.crm_account !== null ? (
                  <LeadStatusMenu
                    accountId={lead.crm_account}
                    accountName={lead.name || lead.normalized_domain}
                    category={lead.outreach?.category ?? "prospect"}
                    size="sm"
                    className="shrink-0"
                  />
                ) : null}
              </div>
              {lead.fair ? (
                <div className="flex flex-wrap items-center gap-1">
                  {lead.fair.url ? (
                    <a
                      href={
                        isHttpUrl(lead.fair.url) ? lead.fair.url : undefined
                      }
                      target="_blank"
                      rel="noreferrer"
                      className="bg-muted text-muted-foreground hover:text-primary text-3xs inline-block max-w-full truncate rounded px-1.5 py-0.5 font-medium tracking-wide uppercase"
                      title={lead.fair.name}
                    >
                      {lead.fair.name}
                    </a>
                  ) : (
                    <span className="bg-muted text-muted-foreground text-3xs inline-block max-w-full truncate rounded px-1.5 py-0.5 font-medium tracking-wide uppercase">
                      {lead.fair.name}
                    </span>
                  )}
                  {lead.fair.proof_url && (
                    <a
                      href={
                        isHttpUrl(lead.fair.proof_url)
                          ? lead.fair.proof_url
                          : undefined
                      }
                      target="_blank"
                      rel="noreferrer"
                      className="text-muted-foreground hover:text-primary text-3xs inline-flex items-center gap-0.5 hover:underline"
                      title={lead.fair.proof_url}
                    >
                      <ExternalLink className="size-2.5" />
                      {tr("fairProofLink")}
                    </a>
                  )}
                </div>
              ) : (
                (lead.keyword || lead.place_label) && (
                  <p className="text-muted-foreground text-3xs font-medium tracking-wide uppercase">
                    {[lead.keyword, lead.place_label]
                      .filter(Boolean)
                      .join(" | ")}
                  </p>
                )
              )}
              {isHttpUrl(lead.website) && (
                <a
                  href={lead.website}
                  target="_blank"
                  rel="noreferrer"
                  className="text-primary block max-w-full truncate text-xs hover:underline"
                  title={lead.website}
                >
                  {truncateUrl(lead.website)}
                </a>
              )}
              {lead.maps_url && (
                <a
                  href={isHttpUrl(lead.maps_url) ? lead.maps_url : undefined}
                  target="_blank"
                  rel="noreferrer"
                  className="text-muted-foreground hover:text-primary inline-flex items-center gap-0.5 text-xs hover:underline"
                >
                  <ExternalLink className="size-3 shrink-0" />
                  {tr("googleProfileLink")}
                </a>
              )}
            </div>
            {/* Capped at the card's width: once it drops under the name on a
                narrow card, the badges wrap onto new lines instead of running
                past the card's edge. */}
            <div className="text-2xs flex max-w-full min-w-0 shrink-0 flex-wrap items-center gap-1.5">
              {lead.batch === null ? (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Badge
                      tone="muted"
                      variant="outline"
                      className="gap-1"
                      tabIndex={0}
                    >
                      <Layers className="size-3" />
                      {tr("importBadge")}
                    </Badge>
                  </TooltipTrigger>
                  {/* Every line at one size, weight and colour. */}
                  <TooltipContent className="max-w-64 font-medium tabular-nums">
                    {deliveredLine}
                    {costLine}
                    <p>{tr("importBadgeHint")}</p>
                  </TooltipContent>
                </Tooltip>
              ) : (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Badge
                      variant="outline"
                      className="gap-1 tabular-nums"
                      tabIndex={0}
                    >
                      <Layers className="size-3" />
                      {tr("batchBadge").replace("{n}", String(lead.batch))}
                    </Badge>
                  </TooltipTrigger>
                  {/* Every line at one size, weight and colour. */}
                  <TooltipContent className="max-w-64 font-medium tabular-nums">
                    {lead.batch_started_at && (
                      <p>
                        {tr("batchStartedAt").replace(
                          "{date}",
                          formatDate(lead.batch_started_at),
                        )}
                      </p>
                    )}
                    {deliveredLine}
                    {costLine}
                    <p>
                      {tr("batchBadgeHint").replace(
                        "{nth}",
                        ordinal(lead.batch, locale),
                      )}
                    </p>
                  </TooltipContent>
                </Tooltip>
              )}
              {/* The extraction icon already says why a left-out lead waits. */}
              {lead.sync_held &&
                !(
                  extractionsActive &&
                  (lead.extraction_segment === "excluded" ||
                    lead.extraction_segment === "pending")
                ) && (
                  <Badge
                    tone="muted"
                    variant="outline"
                    className="gap-1"
                    title={tr("notSyncedHint")}
                  >
                    <CloudOff className="size-3" />
                    {tr("notSynced")}
                  </Badge>
                )}
              <LeadExtractionBadge
                lead={lead}
                extractionNames={extractionNames}
                active={extractionsActive}
              />
              {(lead.enriching || reenriching) && (
                <Badge
                  tone="muted"
                  variant="outline"
                  className="gap-1"
                  title={tr("enrichingHint")}
                >
                  <Loader2 className="size-3 animate-spin" />
                  {tr("enriching")}
                </Badge>
              )}
              {downloadable && (
                <LeadDownloadMenu lead={lead} configurable={editable} />
              )}
            </div>
          </div>
          {/* The growing address pushes the signal badges to the right edge while
          they share its line; once they wrap below, they start on the left. */}
          {(lead.address || hasSignalBadges) && (
            <div className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-1.5">
              {lead.address && (
                <p
                  className="text-muted-foreground flex min-w-0 grow items-center gap-1 text-xs"
                  title={lead.address}
                >
                  <MapPin className="size-3 shrink-0" />
                  <span className="truncate">{lead.address}</span>
                </p>
              )}
              {hasSignalBadges && (
                <div
                  className={cn(
                    "text-2xs flex max-w-full flex-wrap items-center gap-1.5",
                    !lead.address && "sm:ml-auto",
                  )}
                >
                  {lead.job_listing_url && (
                    <a
                      href={
                        isHttpUrl(lead.job_listing_url)
                          ? lead.job_listing_url
                          : undefined
                      }
                      target="_blank"
                      rel="noreferrer"
                      title={lead.job_listing_url}
                    >
                      <Badge tone="primary" variant="outline" className="gap-1">
                        <Briefcase className="size-3" />
                        {tr("jobListingLink")}
                      </Badge>
                    </a>
                  )}
                  {lead.watcher_event && (
                    <WatcherEventBadges event={lead.watcher_event} />
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
      {/* The next step is its own band: a heading at the people's scale, then
          the step (or "nothing planned"), under the same full-bleed hairline
          as the other bands. Only where the next step was read. */}
      {pilot && lead.pilot ? (
        <div className="border-border/70 border-t p-3 text-xs">
          <div className="mb-1 flex min-h-6 items-center">
            <p className="text-foreground text-2sm font-medium">
              {tr("tasksHeading")}
            </p>
          </div>
          <LeadPilotBar
            lead={lead}
            onActivate={
              onOpenAccount && lead.crm_account !== null
                ? () => onOpenAccount(lead.crm_account as number)
                : onPilotActivate
            }
            activation={
              onOpenAccount && lead.crm_account !== null
                ? "openCompany"
                : "showSteps"
            }
          />
        </div>
      ) : null}
      {/* The people sit in their own band under a full-bleed hairline, the
          same line as the accordion's top edge; it hides with the list when
          the list renders nothing. */}
      {(editable || (lead.people && lead.people.length > 0)) && (
        <div className="border-border/70 border-t p-3 empty:hidden">
          <LeadPeopleList
            people={lead.people ?? []}
            phoneRoster={lead.phone_roster ?? []}
            question={lead.custom_query?.text ?? null}
            leadId={lead.id}
            editable={editable}
          />
        </div>
      )}
      {/* The card's detail blocks as one accordion, flush with the card's
          sides and bottom: hairline rows, any number open at once. Hidden when
          no block applies to the lead. */}
      <Accordion type="multiple" variant="flush">
        <CustomQuerySection lead={lead} withVerdict={!isPublic} tr={tr} />
        <OnlinePresenceSection lead={lead} adRegistries={adRegistries} />
        <JobOffersList
          offers={lead.job_offers ?? []}
          checked={lead.job_offers_checked ?? {}}
          tr={tr}
        />
        {!lead.has_website && scoringEnabled ? (
          <LeadSectionNote>
            <span className="text-foreground font-medium">
              {tr("noWebsiteOpportunityTitle")}
            </span>
            <p className="mt-0.5">{tr("noWebsiteOpportunityBody")}</p>
            <p className="mt-0.5">
              {lead.maps_url ? (
                <a
                  href={isHttpUrl(lead.maps_url) ? lead.maps_url : undefined}
                  target="_blank"
                  rel="noreferrer"
                  className="hover:text-primary hover:underline"
                >
                  {tr("gmbPresent")}
                </a>
              ) : (
                tr("gmbAbsent")
              )}
            </p>
          </LeadSectionNote>
        ) : lead.website_quality_breakdown?.skipped === "no_own_website" ? (
          <LeadSectionNote>
            <span className="text-foreground font-medium">
              {tr("opportunityTitle")}
            </span>
            <p className="mt-0.5">{tr("noOwnWebsite")}</p>
          </LeadSectionNote>
        ) : (
          lead.website_quality_breakdown?.opportunities && (
            <OpportunityReport
              report={lead.website_quality_breakdown.opportunities}
              website={lead.website}
              mapsUrl={lead.maps_url}
              leadId={lead.id}
              leadName={lead.name}
              tr={tr}
            />
          )
        )}
        {/* Private to the team: a lead's security findings never reach a
            public page (case studies, showcases). */}
        {lead.security_audit && (
          <LeadSection
            value="security"
            title={<LeadSecurityTitle />}
            meta={<LeadSecurityMeta audit={lead.security_audit} />}
            actions={
              lead.security_audit.report ? (
                <SecurityPdfButton leadId={lead.id} />
              ) : undefined
            }
          >
            <LeadSecurityReport audit={lead.security_audit} />
          </LeadSection>
        )}
        {mailingPeople.length > 0 && (
          <LeadSection
            value="mailing"
            title={tr("mailingCopyToggle")}
            aside={<Mail className="size-3.5" />}
          >
            <MailingCopies people={mailingPeople} />
          </LeadSection>
        )}
        {lead.decision_makers?.length > 0 && (
          <LeadSection
            value="officers"
            title={tr("decisionMakersToggle")}
            meta={lead.decision_makers.length}
            aside={<Users className="size-3.5" />}
          >
            <DecisionMakersView people={lead.decision_makers} tr={tr} />
          </LeadSection>
        )}
        {lead.watcher_event && (
          <LeadSection
            value="watcher"
            title={tr("watcherEventToggle")}
            meta={tr(watcherEventLabelKey(lead.watcher_event))}
            aside={<Radar className="size-3.5" />}
          >
            <WatcherEventDetails event={lead.watcher_event} />
          </LeadSection>
        )}
        <CompanyFicheBlock
          pappers={lead.pappers_fiche}
          societe={lead.societe_fiche}
          tr={tr}
        />
      </Accordion>
    </li>
  )
}

// Decorative blurred placeholder values (aria-hidden), not user-facing copy.
const BLURRED_PLACEHOLDER = {
  website: "https://example-domain.com",
  address: "12 rue Exemple, 75000 Paris",
  emails: "2",
  phones: "1",
  rating: "4.6",
  contact: "Jane Placeholder | Manager",
  contactEmail: "jane@example-domain.com",
}

// Labels for the public case-study showcase, which only ever states what the
// enrichment actually found on that business.
const PUBLIC_FINDING_T = {
  personalEmail: { FR: "email nominatif", EN: "personal email" },
  directLine: { FR: "ligne directe", EN: "direct line" },
  moreNamedOne: {
    FR: "+ 1 autre contact nommé",
    EN: "+ 1 more named contact",
  },
  moreNamedMany: {
    FR: "+ {n} autres contacts nommés",
    EN: "+ {n} more named contacts",
  },
  genericMany: { FR: "{n} adresses génériques", EN: "{n} generic addresses" },
  strongMany: { FR: "{n} forts", EN: "{n} strong" },
  mediumMany: { FR: "{n} moyens", EN: "{n} medium" },
  adsWhy: {
    FR: "Budget d'acquisition engagé en ce moment",
    EN: "An acquisition budget is live right now",
  },
  hiringWhy: {
    FR: "Recrute, donc croissance et budget",
    EN: "Hiring, so growth and budget",
  },
  socialsMany: { FR: "{n} réseaux sociaux", EN: "{n} social accounts" },
  sequenceOne: {
    FR: "Séquence prête pour 1 contact",
    EN: "Sequence ready for 1 contact",
  },
  sequenceMany: {
    FR: "Séquence prête pour {n} contacts",
    EN: "Sequence ready for {n} contacts",
  },
}

// Copy of the showcase block on a public case study. Written for a cold
// visitor: every line is something they could not have found in five minutes.
const FEATURED_LEAD_T = {
  eyebrow: {
    FR: "Un lead à la loupe · choisi parmi {n} entreprises",
    EN: "One lead, up close · picked among {n} businesses",
  },
  verdict: {
    FR: "Pourquoi l'appeler cette semaine",
    EN: "Why call them this week",
  },
  compareTitle: { FR: "Qui joindre", EN: "Who to reach" },
  manualLabel: { FR: "Une recherche manuelle", EN: "A manual search" },
  manualGeneric: {
    FR: "{n} adresses génériques, l'accueil au bout du fil.",
    EN: "{n} generic addresses, the front desk on the line.",
  },
  manualGenericOne: {
    FR: "1 adresse générique, l'accueil au bout du fil.",
    EN: "1 generic address, the front desk on the line.",
  },
  manualNone: {
    FR: "Un formulaire de contact, au mieux.",
    EN: "A contact form, at best.",
  },
  toolLabel: { FR: "AutoLeads", EN: "AutoLeads" },
  verdictKeywordAbsent: {
    FR: "Invisible sur Google pour « {keyword} »",
    EN: "Invisible on Google for “{keyword}”",
  },
  verdictKeywordRanked: {
    FR: "{rank}e sur Google pour « {keyword} »",
    EN: "#{rank} on Google for “{keyword}”",
  },
  verdictKeywordWhy: {
    FR: "Une recherche faite {volume} fois par mois, et leur site n'est pas en première page.",
    EN: "A search made {volume} times a month, and their site is not on page one.",
  },
  maturityTitle: { FR: "Maturité web", EN: "Web maturity" },
  maturityHint: {
    FR: "Ce que vous pouvez leur vendre, mesuré sur leur site et sur Google.",
    EN: "What you can sell them, measured on their site and on Google.",
  },
  searchTitle: { FR: "Sur Google", EN: "On Google" },
  searchMissed: {
    FR: "{volume} recherches par mois où ils ne sont pas en première page",
    EN: "{volume} searches a month where they are not on page one",
  },
  searchKeyword: { FR: "Recherche", EN: "Search" },
  searchVolume: { FR: "Volume / mois", EN: "Volume / month" },
  searchPosition: { FR: "Position", EN: "Position" },
  moreFindingsOne: { FR: "+ 1 autre constat", EN: "+ 1 more finding" },
  moreFindingsMany: { FR: "+ {n} autres constats", EN: "+ {n} more findings" },
  hiringFor: { FR: "{n} postes ouverts", EN: "{n} open roles" },
  hiringForOne: { FR: "1 poste ouvert", EN: "1 open role" },
  auditFull: { FR: "Voir l'audit complet", EN: "See the full audit" },
  auditHide: { FR: "Masquer l'audit complet", EN: "Hide the full audit" },
  factsTitle: { FR: "Fiche", EN: "Fact sheet" },
  factRevenue: { FR: "Chiffre d'affaires", EN: "Revenue" },
  factWorkforce: { FR: "Effectif", EN: "Headcount" },
  factSince: { FR: "Créée en", EN: "Founded" },
  factHiring: { FR: "Recrutement", EN: "Hiring" },
  factAds: { FR: "Publicité", EN: "Ads" },
  factRating: { FR: "Note", EN: "Rating" },
  factSite: { FR: "Score du site", EN: "Site score" },
  factAdsOn: { FR: "Active", EN: "Live" },
  seeRepView: {
    FR: "Voir ce que reçoit le commercial",
    EN: "See what the rep receives",
  },
}

// Enough to make the point; the full audit sits behind its toggle.
const SHOWCASE_PEOPLE_MAX = 4
const SHOWCASE_KEYWORDS_MAX = 6
const SHOWCASE_MEASURED_MIN = 3

// The themes a web professional sells against, in the order they pitch them.
// The two search themes are read as one keyword table instead, and the money
// theme is budget context, not maturity.
const SHOWCASE_SEARCH_THEMES: LeadsOpportunityTheme[] = [
  "visibility_keyword",
  "visibility_geo",
]
const SHOWCASE_DIAGNOSTIC_THEMES: LeadsOpportunityTheme[] = [
  "visibility_ai",
  "speed",
  "compliance",
  "seo_technical",
  "google_profile",
  "conversion",
  "design",
  "acquisition",
  "trust",
]

// A search counts as missed when the site is not on page one for it.
function isMissedSearch(row: LeadsKeywordOpportunity): boolean {
  return (
    row.found === false ||
    (row.found === true && (row.absolute ?? 0) > KEYWORD_RANK_PAGE_ONE)
  )
}

const SCORE_SCALE = "/100"
const RATING_SCALE = "/5"

// "9 155 624 EUR" -> "9,2 M€". The registry stores the figure as display text.
function compactEuros(value: string, locale: string): string | null {
  if (!/EUR|€/.test(value)) return null
  const amount = Number(value.replace(/[^\d]/g, ""))
  if (!amount) return null
  return new Intl.NumberFormat(locale === "FR" ? "fr-FR" : "en-GB", {
    notation: "compact",
    style: "currency",
    currency: "EUR",
    maximumFractionDigits: 1,
  }).format(amount)
}

function ShowcaseHeading({ children }: { children: ReactNode }) {
  return (
    <h4 className="text-muted-foreground text-3xs tracking-wider-caps font-semibold uppercase">
      {children}
    </h4>
  )
}

/**
 * The public case study's showcase: the campaign's best lead, read like an
 * intelligence brief. The verdict first, then who to reach next to what a
 * manual search gives, the budget evidence read off their site, and the hook.
 * A CRM-style fact sheet sits alongside. Contacts are masked by the backend.
 */
export function FeaturedLeadCard({
  lead,
  leadsCount,
  repViewHref,
}: {
  lead: Lead
  leadsCount: number
  // Anchor of the "what the rep sees" panel, which is written for this lead.
  repViewHref?: string
}) {
  const tr = useTr(LEADS_PANE_T)
  const t = useTr(FEATURED_LEAD_T)
  const found = useTr(PUBLIC_FINDING_T)
  const locale = useAutoleadsLocale()
  const [fullAudit, setFullAudit] = useState(false)

  const people = lead.people ?? []
  const named = people
    .filter(
      (person) => person.name && person.role && person.name !== person.role,
    )
    .sort(
      (a, b) =>
        Number(Boolean(b.from_decision_makers)) -
          Number(Boolean(a.from_decision_makers)) ||
        Number(Boolean(b.has_email)) - Number(Boolean(a.has_email)) ||
        Number(Boolean(b.has_phone)) - Number(Boolean(a.has_phone)),
    )
  const generic = people.length - named.length
  const roles = (lead.job_offers ?? [])
    .map((offer) => offer.title)
    .filter(Boolean)
  const adPlatforms = (lead.ad_check?.platforms ?? [])
    .map((entry) => entry.platform)
    .filter(Boolean)
  const audit = lead.website_quality_breakdown?.opportunities
  const themes = audit?.themes ?? []
  // One table for every search the audit measured, busiest first; the same
  // wording can appear on both search axes.
  const keywords = [
    ...new Map(
      themes
        .filter((theme) => SHOWCASE_SEARCH_THEMES.includes(theme.key))
        .flatMap((theme) => theme.items.flatMap((item) => item.keywords ?? []))
        .map((row) => [row.keyword.toLowerCase(), row] as const),
    ).values(),
  ].sort(
    (a, b) =>
      Number(b.found !== undefined) - Number(a.found !== undefined) ||
      b.search_volume - a.search_volume,
  )
  // A row the SERP walk has not reached yet only carries demand. Measured
  // placements are the proof, so once there are enough of them they stand alone.
  const measured = keywords.filter((row) => row.found !== undefined)
  const shownKeywords = (
    measured.length >= SHOWCASE_MEASURED_MIN ? measured : keywords
  ).slice(0, SHOWCASE_KEYWORDS_MAX)
  const missed = keywords.filter(isMissedSearch)
  const missedVolume = missed.reduce((sum, row) => sum + row.search_volume, 0)
  const diagnostics = SHOWCASE_DIAGNOSTIC_THEMES.flatMap((key) =>
    themes.filter((theme) => theme.key === key && theme.items.length > 0),
  )
  const registryRows = (
    lead.pappers_fiche ?? lead.societe_fiche
  )?.sections?.flatMap((section) => section.rows)
  const registryValue = (key: string) =>
    registryRows?.find((row) => row.key === key && row.value)?.value ?? ""
  const revenue = compactEuros(registryValue("revenue"), locale)
  const foundedYear = registryValue("created_at").slice(0, 4)
  const signals = lead.signals?.[locale] ?? []
  // The busiest search they lose is the most concrete pitch a web professional
  // can make; the writer's strongest pick stands in when nothing was measured.
  const topMissed = missed[0]
  const verdict = topMissed
    ? {
        label: (topMissed.found
          ? t("verdictKeywordRanked").replace(
              "{rank}",
              String(topMissed.absolute),
            )
          : t("verdictKeywordAbsent")
        ).replace("{keyword}", topMissed.keyword),
        why: t("verdictKeywordWhy").replace(
          "{volume}",
          String(topMissed.search_volume),
        ),
      }
    : (signals.find((signal) => signal.strength === "strong") ??
      signals[0] ??
      null)

  const facts = [
    revenue && { key: "revenue", label: t("factRevenue"), value: revenue },
    registryValue("workforce") && {
      key: "workforce",
      label: t("factWorkforce"),
      value: registryValue("workforce"),
    },
    foundedYear && { key: "since", label: t("factSince"), value: foundedYear },
    roles.length > 0 && {
      key: "hiring",
      label: t("factHiring"),
      value: (roles.length > 1 ? t("hiringFor") : t("hiringForOne")).replace(
        "{n}",
        String(roles.length),
      ),
    },
    adPlatforms.length > 0 && {
      key: "ads",
      label: t("factAds"),
      value: `${t("factAdsOn")} · ${adPlatformList(adPlatforms)}`,
    },
    lead.rating !== null && {
      key: "rating",
      label: t("factRating"),
      value: `${lead.rating}${RATING_SCALE}${lead.reviews_count ? ` (${lead.reviews_count})` : ""}`,
    },
    lead.website_quality_score !== null && {
      key: "site",
      label: t("factSite"),
      value: `${lead.website_quality_score}${SCORE_SCALE}`,
    },
  ].filter((fact): fact is { key: string; label: string; value: string } =>
    Boolean(fact),
  )

  return (
    <article className="bg-card ring-border/60 overflow-hidden rounded-xl border ring-1">
      <header className="border-border/60 flex flex-wrap items-center justify-between gap-3 border-b px-5 py-4">
        <div className="min-w-0">
          <p className="text-primary-cta text-3xs tracking-wider-caps inline-flex items-center gap-1.5 font-semibold uppercase">
            <Sparkles className="size-3.5" aria-hidden />
            {t("eyebrow").replace("{n}", String(leadsCount))}
          </p>
          <h3 className="text-foreground mt-1 flex items-center gap-2 text-lg font-semibold tracking-tight">
            <SiteFavicon
              website={lead.website}
              domain={lead.normalized_domain}
            />
            <span className="truncate">
              {lead.name || lead.normalized_domain}
            </span>
          </h3>
        </div>
        {isHttpUrl(lead.website) && (
          <a
            href={lead.website}
            target="_blank"
            rel="noreferrer"
            className="text-muted-foreground hover:text-primary inline-flex max-w-full items-center gap-1 truncate text-xs"
            title={lead.website}
          >
            <Globe className="size-3.5 shrink-0" aria-hidden />
            {lead.normalized_domain || truncateUrl(lead.website)}
          </a>
        )}
      </header>

      <div className="grid lg:grid-cols-[minmax(0,1fr)_17rem]">
        <div className="min-w-0 space-y-7 px-5 py-6">
          {verdict && (
            <div className="border-l-primary-cta border-l-2 pl-4">
              <ShowcaseHeading>{t("verdict")}</ShowcaseHeading>
              <p className="text-foreground font-display tracking-dense mt-1.5 text-xl leading-snug font-semibold text-balance sm:text-2xl">
                {verdict.label}
              </p>
              {verdict.why ? (
                <p className="text-muted-foreground mt-1.5 text-sm">
                  {verdict.why}
                </p>
              ) : null}
            </div>
          )}

          {named.length > 0 && (
            <div className="space-y-3">
              <ShowcaseHeading>{t("compareTitle")}</ShowcaseHeading>
              <div className="grid gap-3 sm:grid-cols-[minmax(0,2fr)_minmax(0,3fr)]">
                <div className="border-border/60 self-start rounded-lg border border-dashed px-4 py-3">
                  <p className="text-muted-foreground text-xs font-medium">
                    {t("manualLabel")}
                  </p>
                  <p className="text-muted-foreground mt-2 text-sm leading-relaxed">
                    {generic > 1
                      ? t("manualGeneric").replace("{n}", String(generic))
                      : generic === 1
                        ? t("manualGenericOne")
                        : t("manualNone")}
                  </p>
                </div>
                <div className="border-primary-cta/25 bg-primary-cta/5 rounded-lg border px-4 py-3">
                  <p className="text-primary-cta text-xs font-semibold">
                    {t("toolLabel")}
                  </p>
                  <ul className="mt-2 space-y-2">
                    {named
                      .slice(0, SHOWCASE_PEOPLE_MAX)
                      .map((person, index) => (
                        <li
                          key={`${person.name}-${index}`}
                          className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-0.5"
                        >
                          <span className="text-sm">
                            <span className="text-foreground font-semibold">
                              {person.name}
                            </span>
                            <span className="text-muted-foreground">
                              {" "}
                              · {person.role}
                            </span>
                          </span>
                          {(person.has_email || person.has_phone) && (
                            <span className="text-muted-foreground inline-flex items-center gap-2.5 text-xs select-none">
                              {person.has_email && (
                                <span className="inline-flex items-center gap-1">
                                  <Lock className="size-3 shrink-0" />
                                  {found("personalEmail")}
                                </span>
                              )}
                              {person.has_phone && (
                                <span className="inline-flex items-center gap-1">
                                  <Lock className="size-3 shrink-0" />
                                  {found("directLine")}
                                </span>
                              )}
                            </span>
                          )}
                        </li>
                      ))}
                  </ul>
                  {named.length > SHOWCASE_PEOPLE_MAX && (
                    <p className="text-muted-foreground mt-2 text-xs">
                      {named.length - SHOWCASE_PEOPLE_MAX > 1
                        ? found("moreNamedMany").replace(
                            "{n}",
                            String(named.length - SHOWCASE_PEOPLE_MAX),
                          )
                        : found("moreNamedOne")}
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}

          {audit && (keywords.length > 0 || diagnostics.length > 0) && (
            <div className="space-y-4">
              <div>
                <ShowcaseHeading>{t("maturityTitle")}</ShowcaseHeading>
                <p className="text-muted-foreground mt-0.5 text-xs">
                  {t("maturityHint")}
                </p>
              </div>

              {keywords.length > 0 && (
                <div className="border-border/60 overflow-x-auto rounded-lg border">
                  <div className="border-border/60 bg-muted/30 flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1 border-b px-4 py-2.5">
                    <p className="text-foreground inline-flex items-center gap-1.5 text-sm font-semibold">
                      <Search className="size-3.5" aria-hidden />
                      {t("searchTitle")}
                    </p>
                    {missedVolume > 0 && (
                      <p className="text-destructive text-xs font-medium tabular-nums">
                        {t("searchMissed").replace(
                          "{volume}",
                          missedVolume.toLocaleString(
                            locale === "FR" ? "fr-FR" : "en-GB",
                          ),
                        )}
                      </p>
                    )}
                  </div>
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-muted-foreground text-2xs text-left">
                        <th className="px-4 py-2 font-medium">
                          {t("searchKeyword")}
                        </th>
                        <th className="px-4 py-2 text-right font-medium">
                          {t("searchVolume")}
                        </th>
                        <th className="px-4 py-2 text-right font-medium">
                          {t("searchPosition")}
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-border/60 divide-y">
                      {shownKeywords.map((row) => (
                        <tr key={row.keyword}>
                          <td className="text-foreground px-4 py-2">
                            {row.keyword}
                          </td>
                          <td className="text-muted-foreground px-4 py-2 text-right tabular-nums">
                            {row.search_volume}
                          </td>
                          <td
                            className={cn(
                              "px-4 py-2 text-right font-semibold whitespace-nowrap tabular-nums",
                              keywordPositionTone(row),
                            )}
                          >
                            {keywordPositionLabel(row, tr)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {diagnostics.length > 0 && (
                <ul className="grid gap-3 sm:grid-cols-2">
                  {diagnostics.map((theme) => {
                    const Icon = OPPORTUNITY_THEME_ICONS[theme.key]
                    const group = severityGroup(theme.severity)
                    const [first, ...rest] = theme.items
                    return (
                      <li
                        key={theme.key}
                        className="border-border/60 min-w-0 rounded-lg border px-4 py-3"
                      >
                        <div className="flex items-center justify-between gap-2">
                          <p className="text-muted-foreground text-2xs inline-flex items-center gap-1.5 font-medium">
                            <Icon className="size-3.5 shrink-0" aria-hidden />
                            {tr(OPPORTUNITY_THEME_LABELS[theme.key])}
                          </p>
                          <span
                            className={cn(
                              "text-3xs shrink-0 rounded-full px-2 py-0.5 font-semibold",
                              group.chip,
                            )}
                          >
                            {tr(group.labelKey)}
                          </span>
                        </div>
                        <p className="text-foreground mt-1.5 text-sm font-semibold">
                          {first.title}
                        </p>
                        <p className="text-muted-foreground mt-0.5 text-xs leading-relaxed">
                          {first.detail}
                        </p>
                        {rest.length > 0 && (
                          <p className="text-muted-foreground/80 text-2xs mt-1.5">
                            {rest.length > 1
                              ? t("moreFindingsMany").replace(
                                  "{n}",
                                  String(rest.length),
                                )
                              : t("moreFindingsOne")}
                          </p>
                        )}
                      </li>
                    )
                  })}
                </ul>
              )}

              <button
                type="button"
                onClick={() => setFullAudit((v) => !v)}
                aria-expanded={fullAudit}
                className="text-primary-cta inline-flex items-center gap-1 text-xs font-medium hover:underline"
              >
                {fullAudit ? (
                  <ChevronDown className="size-3.5" aria-hidden />
                ) : (
                  <ChevronRight className="size-3.5" aria-hidden />
                )}
                {fullAudit ? t("auditHide") : t("auditFull")}
                <span className="text-muted-foreground font-normal">
                  {" "}
                  ·{" "}
                  {(audit.count > 1
                    ? tr("opportunitySignalCount")
                    : tr("opportunitySignalCountOne")
                  ).replace("{n}", String(audit.count))}
                </span>
              </button>
              {fullAudit && (
                <OpportunityReport
                  report={audit}
                  website={lead.website}
                  mapsUrl={lead.maps_url}
                  tr={tr}
                  defaultOpen
                />
              )}
            </div>
          )}
        </div>

        <aside className="border-border/60 bg-muted/20 flex flex-col border-t lg:border-t-0 lg:border-l">
          {facts.length > 0 && (
            <div className="px-5 py-6">
              <ShowcaseHeading>{t("factsTitle")}</ShowcaseHeading>
              <dl className="mt-3 space-y-3">
                {facts.map((fact) => (
                  <div key={fact.key}>
                    <dt className="text-muted-foreground text-2xs">
                      {fact.label}
                    </dt>
                    <dd className="text-foreground text-sm font-semibold tabular-nums">
                      {fact.value}
                    </dd>
                  </div>
                ))}
              </dl>
            </div>
          )}
          {lead.mailing_contacts > 0 && (
            <div className="border-border/60 mt-auto space-y-2 border-t px-5 py-4">
              <p className="text-foreground inline-flex items-center gap-1.5 text-xs font-medium">
                <Send
                  className="text-primary-cta size-3.5 shrink-0"
                  aria-hidden
                />
                {lead.mailing_contacts > 1
                  ? found("sequenceMany").replace(
                      "{n}",
                      String(lead.mailing_contacts),
                    )
                  : found("sequenceOne")}
              </p>
              {repViewHref ? (
                <a
                  href={repViewHref}
                  className="text-primary-cta inline-flex items-center gap-1 text-xs font-medium hover:underline"
                >
                  {t("seeRepView")}
                  <ChevronDown className="size-3.5" aria-hidden />
                </a>
              ) : null}
            </div>
          )}
        </aside>
      </div>
    </article>
  )
}

function BufferedLeadCard({ lead }: { lead: Lead }) {
  const tr = useTr(LEADS_PANE_T)
  return (
    <li className="border-border bg-card/40 relative overflow-hidden rounded-md border p-3">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0 space-y-0.5">
          <div className="flex items-center gap-2 text-sm font-medium">
            <Building2 className="text-muted-foreground size-3.5 shrink-0" />
            <span className="truncate">
              {lead.name || tr("leadNumber").replace("{id}", String(lead.id))}
            </span>
            <Tooltip>
              <TooltipTrigger asChild>
                <Badge
                  tone="muted"
                  variant="outline-dashed"
                  className="text-3xs tracking-wide uppercase"
                >
                  {tr("statusBuffered")}
                </Badge>
              </TooltipTrigger>
              <TooltipContent className="max-w-64">
                {tr("statusBufferedHint")}
              </TooltipContent>
            </Tooltip>
          </div>
        </div>
      </div>
      <div
        aria-hidden
        className="pointer-events-none mt-2 space-y-1.5 blur-sm select-none"
      >
        <p className="text-muted-foreground flex items-center gap-1 text-xs">
          <Globe className="size-3" />
          <span className="truncate">{BLURRED_PLACEHOLDER.website}</span>
        </p>
        <p className="text-muted-foreground flex items-center gap-1 text-xs">
          <MapPin className="size-3" />
          <span className="truncate">{BLURRED_PLACEHOLDER.address}</span>
        </p>
        <div className="text-2xs flex flex-wrap gap-1.5">
          <Badge variant="outline" className="gap-1">
            <Mail className="size-3" /> {BLURRED_PLACEHOLDER.emails}
          </Badge>
          <Badge variant="outline" className="gap-1">
            <Phone className="size-3" /> {BLURRED_PLACEHOLDER.phones}
          </Badge>
          <Badge variant="outline" className="gap-1">
            <Star className="size-3" />
            {BLURRED_PLACEHOLDER.rating}
          </Badge>
        </div>
        <div className="border-border/70 bg-background rounded-sm border px-2 py-1.5 text-xs">
          <div className="font-medium">{BLURRED_PLACEHOLDER.contact}</div>
          <div className="text-muted-foreground flex items-center gap-1">
            <Mail className="size-3" />
            {BLURRED_PLACEHOLDER.contactEmail}
          </div>
        </div>
      </div>
      <p className="text-muted-foreground text-2xs mt-2">{tr("topUpUnlock")}</p>
    </li>
  )
}
